import { useState, useEffect } from "react";
import { LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer } from "recharts";
import TopBar from "../components/TopBar";
import Avatar from "../components/Avatar";
import Badge from "../components/Badge";
import Toast from "../components/Toast";
import { CLIENT_COLORS } from "../constants";
import { apiFetch } from "../utils/api";
import { loadWatermarkDataUrl, applyPdfWatermark } from "../utils/pdfWatermark";
import { todayIST } from "../utils/dateUtils";


// ── CDN loader helpers (same pattern as Reports page) ────────────────────────
const loadExcelJS = () => new Promise((resolve, reject) => {
  if (window.ExcelJS) { resolve(window.ExcelJS); return; }
  const s = document.createElement("script");
  s.src = "https://cdnjs.cloudflare.com/ajax/libs/exceljs/4.4.0/exceljs.min.js";
  s.onload = () => resolve(window.ExcelJS); s.onerror = reject;
  document.head.appendChild(s);
});

const loadJsPDF = () => new Promise((resolve, reject) => {
  if (window.jspdf && window.jspdf.jsPDF) { resolve(window.jspdf.jsPDF); return; }
  const s1 = document.createElement("script");
  s1.src = "https://cdnjs.cloudflare.com/ajax/libs/jspdf/2.5.1/jspdf.umd.min.js";
  s1.onload = () => {
    const s2 = document.createElement("script");
    s2.src = "https://cdnjs.cloudflare.com/ajax/libs/jspdf-autotable/3.8.2/jspdf.plugin.autotable.min.js";
    s2.onload = () => resolve(window.jspdf.jsPDF); s2.onerror = reject;
    document.head.appendChild(s2);
  }; s1.onerror = reject;
  document.head.appendChild(s1);
});

function triggerDownload(blob, filename) {
  const url = URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = url; a.download = filename;
  document.body.appendChild(a); a.click();
  document.body.removeChild(a); URL.revokeObjectURL(url);
}

const Scoring = ({ topBarProps, cycles, setCycles, clients, employees, cycleEmailState, allReviews, setAllReviews, scoringHikes, setScoringHikes, scoringLocked, setScoringLocked }) => {

  // ── Cycle selector — Active + Closed only ───────────────────────────────────
  const scoringCycles = (cycles || [])
    .filter(c => c.status === "Active" || c.status === "Closed")
    .slice().sort((a, b) => {
      const ord = { "Q1": 1, "Q2": 2, "Q3": 3, "Q4": 4 };
      if (a.year !== b.year) return b.year - a.year;
      return (ord[(b.q || "").split(" ")[0]] || 0) - (ord[(a.q || "").split(" ")[0]] || 0);
    });
  const activeCycle = (cycles || []).find(c => c.status === "Active");
  const [selCycId, setSelCycId] = useState(activeCycle ? activeCycle.id : (scoringCycles.length > 0 ? scoringCycles[0].id : ""));
  const selCycle = (cycles || []).find(c => c.id === selCycId) || null;
  const quarter = selCycle ? selCycle.q : "";
  const isHistorical = selCycle ? !!selCycle.closed : false;

  // All available quarters from cycles prop (most recent first → reverse for chronological)
  const allQtrs = scoringCycles.map(c => c.q).filter(Boolean);
  const [comps, setComps] = useState([]);
  const [empScores, setEmpScores] = useState([]);
  const [scoringLoading, setScoringLoading] = useState(false);
  const [dirty, setDirty] = useState(false);

  // Fetch competencies from API on mount
  useEffect(() => {
    apiFetch("/api/scoring/competencies")
      .then(r => r.json())
      .then(d => {
        if (d.success && d.data && d.data.length > 0)
          setComps(d.data.map(c => ({ ...c, name: c.name, weight: Number(c.weight) })));
      })
      .catch(() => { });
  }, []); // eslint-disable-line

  // Sync selCycId when cycles load (e.g. from API)
  useEffect(() => {
    if (!selCycId && scoringCycles.length > 0) {
      const active = scoringCycles.find(c => c.status === "Active");
      setSelCycId(active ? active.id : scoringCycles[0].id);
    }
  }, [cycles]); // eslint-disable-line

  // Fetch scoring data from API when cycle selection changes
  useEffect(() => {
    if (!selCycId) { setEmpScores([]); return; }
    setScoringLoading(true);
    apiFetch(`/api/scoring?cycle_id=${selCycId}`)
      .then(r => r.json())
      .then(d => {
        if (d.success && d.data) {
          const qt = selCycle ? selCycle.q : "";
          const rows = d.data.map(row => ({
            id: row.employee_id,          // internal DB id — used in API calls
            code: row.employee_code,      // display code — used for UI state keys
            name: row.employee_name,
            ctc: Number(row.ctc_at_time || row.base_ctc || 0),
            score: row.score != null ? parseFloat(row.score) : null,
            approvedHike: row.approved_hike != null ? String(row.approved_hike) : null,
          }));
          setEmpScores(rows);
          // Pre-populate hike + lock state from API data
          const hikeUpd = {}, lockUpd = {};
          d.data.forEach(row => {
            if (row.approved_hike != null) hikeUpd[row.employee_code] = String(row.approved_hike);
            if (row.scoring_locked) lockUpd[row.employee_code] = true;
          });
          if (Object.keys(hikeUpd).length) setScoringHikes(p => ({ ...p, [qt]: { ...(p[qt] || {}), ...hikeUpd } }));
          if (Object.keys(lockUpd).length) setScoringLocked(p => ({ ...p, [qt]: { ...(p[qt] || {}), ...lockUpd } }));
        }
      })
      .catch(() => { })
      .finally(() => setScoringLoading(false));
  }, [selCycId]); // eslint-disable-line
  const [toast, setToast] = useState("");
  const [showTrend, setShowTrend] = useState(false);
  const [showGuide, setShowGuide] = useState(false);
  const [guideStep, setGuideStep] = useState(0);
  const [showPublishModal, setShowPublishModal] = useState(false);
  // Per-employee scoring breakdown drawer state
  const [breakdownEmp, setBreakdownEmp] = useState(null);          // { id, code, name }
  const [breakdownData, setBreakdownData] = useState(null);
  const [breakdownLoading, setBreakdownLoading] = useState(false);
  const [breakdownError, setBreakdownError] = useState("");
  const openBreakdown = (emp) => {
    setBreakdownEmp(emp);
    setBreakdownData(null);
    setBreakdownError("");
    setBreakdownLoading(true);
    apiFetch(`/api/scoring/${emp.id}/breakdown?cycle_id=${selCycId}`)
      .then(r => r.json())
      .then(d => {
        if (d.success) setBreakdownData(d.data);
        else setBreakdownError(d.message || "Failed to load breakdown");
      })
      .catch(() => setBreakdownError("Network error — could not load breakdown"))
      .finally(() => setBreakdownLoading(false));
  };
  const closeBreakdown = () => { setBreakdownEmp(null); setBreakdownData(null); setBreakdownError(""); };
  const [toastType, setToastType] = useState("");
  const showToast = (msg, type = "") => { setToast(msg); setToastType(type); setTimeout(() => { setToast(""); setToastType(""); }, 2800); };

  // ── Scoring Walkthrough ─────────────────────────────────────────────────────
  const GUIDE_STEPS = [
    {
      icon: "🎯", title: "What is Scoring & Hikes?", color: "#E8520A",
      body: "This module is the heart of the EPR process. It calculates each employee's hike percentage based on how stakeholders rated their performance across defined competencies.",
      tip: "Think of it as translating client satisfaction into fair, data-backed compensation decisions."
    },
    {
      icon: "⚖️", title: "Step 1 — Set Competency Weights", color: "#3B82F6",
      body: "Define how much each competency matters. Six competencies are pre-loaded. Their weights must add up to exactly 100%. You can adjust them each quarter — but closed quarters are locked.",
      tip: "Example: If 'Client Satisfaction' carries 30% weight, it influences the final score 3× more than a 10% competency."
    },
    {
      icon: "📊", title: "Step 2 — Score Column", color: "#8B5CF6",
      body: "Each employee's Score (0–100) is a weighted average of all criteria rated by their stakeholder. Green = 85+, Amber = 70–84, Red = below 70.",
      tip: "A score of 85+ is excellent. Below 60 warrants a conversation."
    },
    {
      icon: "💰", title: "Step 3 — Hike Formula", color: "#10B981",
      body: "Suggested hike = (Score ÷ 100) × 4 × 6. For a score of 85: (85÷100)×4×6 = 20.4%. Click ✓ on any row to apply the suggestion, or type a custom override.",
      tip: "Use the per-row ✓ button for individual approval, or Bulk Approve to apply all suggestions at once."
    },
    {
      icon: "🔒", title: "Step 4 — Lock & Publish", color: "#F59E0B",
      body: "Lock individual rows once finalised — locked rows cannot be changed. When all rows are locked, use Publish Quarter to close scoring and automatically mark all reviews as Closed.",
      tip: "Best practice: Review each employee, adjust where needed, lock, then publish."
    },
    {
      icon: "📈", title: "Step 5 — Trend Analysis", color: "#0D1B2A",
      body: "Click 'Show Trends' to see each employee's score and hike move quarter-over-quarter. Upward trends signal growth; flat or declining trends are early signals to investigate.",
      tip: "Employees with consistent upward trends for 3+ quarters are top performers for promotions."
    },
  ];

  // empScores: use API data only — no hardcoded fallback. Default ordering is
  // Employee Id A → Z so the appraisal table matches the Employee Database
  // page's natural order.
  const displayScores = [...empScores].sort((a, b) =>
    String(a.code || "").localeCompare(String(b.code || ""), undefined, { numeric: true, sensitivity: "base" })
  );
  const MULT = 6;
  const totalW = comps.reduce((s, c) => s + Number(c.weight), 0);
  const weightOk = totalW === 100;

  // ── Review status per employee (mirrors Review Management priority rollup) ──
  // "Least-complete wins" — if employee has 2 stakeholders, one Submitted and
  // one Initiated, show "Initiated" (the lower-progress one) so the admin
  // knows the employee isn't ready for scoring yet. Higher index = less complete.
  const SCORE_STATUS_PRIORITY = ["Closed", "Submitted", "In Progress", "Initiated", "Not Started", "Overdue"];

  // Per-employee request-log helper (mirrors the Reviews.jsx logic). Backend
  // writes log.type = "request"; optimistic UI writes "Review Request".
  const _normLogType = t => String(t || "").trim().toLowerCase().replace(/\s+/g, "");
  const _isRequestLog = l => { const s = _normLogType(l && l.type); return s === "request" || s === "reviewrequest"; };

  const getEmpReviewStatus = (empCode) => {
    if (scoringLocked[quarter] && scoringLocked[quarter][empCode]) return "Closed";

    const emp = (employees || []).find(e => e.code === empCode);
    if (!emp) return "Not Started";

    const statuses = [];

    (emp.allocations || []).forEach(alloc => {
      const cl = (clients || []).find(c => c.id === alloc.clientId);
      if (!cl) return;

      // Resolve stakeholders — same rules as getEmpStakeholders in Reviews
      const active = cl.stakeholders.filter(s => s.active);
      let stakeholders = [];

      if (alloc.stakeholders && alloc.stakeholders.length > 0) {
        stakeholders = active.filter(sh => alloc.stakeholders.some(s => s.stakeholderId === sh.id));
      } else {
        const pid = cl.primaryStakeholderId
          ? cl.primaryStakeholderId
          : ((active.filter(s => s.level === "client")[0]) || {}).id;
        const reviews = allReviews || [];
        stakeholders = active.filter(sh => {
          if (sh.level === "client") return pid && pid === sh.id;
          if (sh.level === "dept" && sh.deptId === alloc.deptId) {
            const hasRecord = reviews.some(r =>
              r.empId === empCode && r.client === cl.name && r.stakeholder === sh.name && r.quarter === quarter
            );
            if (hasRecord) return true;
            const key = selCycId + "_" + alloc.clientId + "_" + sh.id;
            const es = (cycleEmailState || {})[key] || {};
            return (es.logs || []).some(l => l.empId === empCode);
          }
          return false;
        });
      }

      // Collect each stakeholder's review status into statuses[]
      stakeholders.forEach(sh => {
        const rev = (allReviews || []).find(r =>
          r.empId === empCode && r.client === cl.name && r.stakeholder === sh.name && r.quarter === quarter
        );
        let status = "Not Started";
        if (rev) {
          if (rev.status === "Approved") status = "Closed";
          else if (rev.status === "Submitted") status = "Submitted";
          else if (rev.status === "In Progress") status = "In Progress";
          else if (rev.status === "Initiated") status = "Initiated";
          else status = "Not Started";
        } else {
          // No reviews row yet — check per-employee request log (avoids
          // leaking another employee's request_at from the shared state row).
          const key = selCycId + "_" + alloc.clientId + "_" + sh.id;
          const es = (cycleEmailState || {})[key] || {};
          const requested = (es.logs || []).some(l => l.empId === empCode && _isRequestLog(l));
          if (requested) status = "Initiated";
        }
        statuses.push(status);
      });
    });

    if (statuses.length === 0) return "Not Started";

    // Pick the least-complete status by reducing with the priority index.
    // (Higher index = less complete.) Fixes the prior bug where the seed
    // value "Not Started" was already at the top of the index range so
    // nothing ever beat it, and Submitted/Initiated reviews were silently
    // ignored.
    return statuses.reduce((acc, s) =>
      SCORE_STATUS_PRIORITY.indexOf(s) > SCORE_STATUS_PRIORITY.indexOf(acc) ? s : acc
    );
  };

  // ── Score color coding ───────────────────────────────────────────────────────
  const scoreColor = (s) => s >= 85 ? "#10B981" : s >= 70 ? "#E8520A" : "#EF4444";
  const scoreBg = (s) => s >= 85 ? "#F0FDF4" : s >= 70 ? "#FFF5F0" : "#FEF2F2";
  const scoreBdr = (s) => s >= 85 ? "#BBF7D0" : s >= 70 ? "#FDBA74" : "#FECACA";

  // ── Review status badge ──────────────────────────────────────────────────────
  const REV_CLR = {
    "Not Started": { bg: "#F8FAFC", tc: "#94A3B8", bdr: "#E2E8F0" },
    "Initiated": { bg: "#EEF2FF", tc: "#4338CA", bdr: "#C7D2FE" },
    "In Progress": { bg: "#FEF3C7", tc: "#92400E", bdr: "#FDE68A" },
    "Submitted": { bg: "#DBEAFE", tc: "#1E40AF", bdr: "#BFDBFE" },
    "Closed": { bg: "#D1FAE5", tc: "#065F46", bdr: "#A7F3D0" },
  };
  const RevBadge = ({ status }) => {
    const c = REV_CLR[status] || REV_CLR["Not Started"];
    return (
      <span style={{
        fontSize: 10, fontWeight: 700, background: c.bg, color: c.tc,
        border: "1px solid " + c.bdr, borderRadius: 100, padding: "2px 8px",
        display: "inline-flex", alignItems: "center", gap: 4, whiteSpace: "nowrap"
      }}>
        <span style={{ width: 5, height: 5, borderRadius: "50%", background: c.tc, display: "inline-block" }} />
        {status}
      </span>
    );
  };

  // ── Weight helpers ───────────────────────────────────────────────────────────
  const updW = (i, v) => { if (isHistorical) return; setComps(p => p.map((c, j) => j === i ? { ...c, weight: Math.max(0, Math.min(100, Number(v) || 0)) } : c)); setDirty(true); };
  const saveW = async () => {
    if (!weightOk) { showToast("Weights must total 100%", "error"); return; }
    try {
      const res = await apiFetch("/api/scoring/competencies", { method: "PUT", body: JSON.stringify({ competencies: comps.map(c => ({ id: c.id, weight: c.weight })) }) });
      const d = await res.json();
      if (!d.success) { showToast("Error: " + (d.message || "Save failed"), "error"); return; }
      setDirty(false); showToast("Weights saved");
    } catch (e) { showToast("Network error — weights not saved", "error"); }
  };

  // ── Hike helpers ─────────────────────────────────────────────────────────────
  const getHike = code => (scoringHikes[quarter] ? scoringHikes[quarter][code] : undefined);
  const setHike = (code, v) => setScoringHikes(p => ({ ...p, [quarter]: { ...(p[quarter] || {}), [code]: v } }));
  const isLocked = code => (scoringLocked[quarter] ? !!scoringLocked[quarter][code] : false);

  const applySuggested = (code, score) => {
    if (!isLocked(code) && score !== null) {
      setHike(code, ((score / 100) * 4 * MULT).toFixed(2));
    }
  };

  const toggleLk = async (empId, code) => {
    const nowLocked = !isLocked(code);
    const prevLocked = { ...scoringLocked };
    const prevReviews = setAllReviews ? undefined : null;
    setScoringLocked(p => ({ ...p, [quarter]: { ...(p[quarter] || {}), [code]: nowLocked } }));
    // Do NOT update allReviews on lock/unlock — Reviews page must show real DB statuses.
    // Scoring page derives its display status from scoringLocked independently (getEmpReviewStatus).
    // On unlock the least-priority status (e.g. "Initiated") will show correctly in Reviews.
    const hikeValue = (nowLocked && scoringHikes && scoringHikes[quarter]) ? scoringHikes[quarter][code] : undefined;
    try {
      if (nowLocked) {
        // ── Save hike BEFORE locking — approveHike blocks updates when scoring_locked=1 ──
        if (hikeValue !== undefined) {
          const res1 = await apiFetch(`/api/scoring/${empId}/hike`, {
            method: "PUT",
            body: JSON.stringify({ cycle_id: selCycId, approved_hike: hikeValue }),
          });
          const d1 = await res1.json();
          if (!d1.success) { setScoringLocked(prevLocked); showToast("Error saving hike: " + (d1.message || ""), "error"); return; }
        }
        // ── Then lock ──────────────────────────────────────────────────────────────────
        const res = await apiFetch(`/api/scoring/${empId}/lock`, {
          method: "PUT",
          body: JSON.stringify({ cycle_id: selCycId, lock: true }),
        });
        const d = await res.json();
        if (!d.success) { setScoringLocked(prevLocked); showToast("Error: " + (d.message || "Lock failed"), "error"); return; }
      } else {
        const res = await apiFetch(`/api/scoring/${empId}/unlock`, {
          method: "PUT",
          body: JSON.stringify({ cycle_id: selCycId }),
        });
        const d = await res.json();
        if (!d.success) { setScoringLocked(prevLocked); showToast("Error: " + (d.message || "Unlock failed"), "error"); return; }
      }
      showToast(nowLocked ? "Hike locked ✓" : "Row unlocked — review status reflects actual DB state");
    } catch (e) { setScoringLocked(prevLocked); showToast("Network error — lock state not saved", "error"); }
  };

  const bulkApprove = () => {
    const updates = {};
    displayScores.forEach(e => { if (e.score !== null && !isLocked(e.code)) updates[e.code] = ((e.score / 100) * 4 * MULT).toFixed(2); });
    setScoringHikes(p => ({ ...p, [quarter]: { ...(p[quarter] || {}), ...updates } }));
    showToast("All suggested hikes applied — review and lock each row");
  };

  // ── Export Scoring Data ──────────────────────────────────────────────────────
  const exportScoringXLS = async () => {
    if (!displayScores.length) { showToast("No scoring data to export", "error"); return; }
    showToast("Generating Excel…");
    const dateStr = todayIST();
    try {
      const ExcelJS = await loadExcelJS();
      const wb = new ExcelJS.Workbook();
      wb.creator = "Dolluz Corp EPR";
      const ws = wb.addWorksheet(quarter || "Scoring", { views: [{ state: "frozen", ySplit: 1 }] });

      const headers = ["Employee ID", "Employee", "CTC (₹/mo)", "Score /100", "Rating /4", "Suggested %", "Approved Hike %", "New CTC", "Row Status", "Review Status"];
      const colWidths = [12, 22, 14, 12, 12, 14, 16, 14, 14, 16];
      ws.columns = headers.map((h, i) => ({ key: h, width: colWidths[i] }));

      // Header row
      const hRow = ws.getRow(1);
      hRow.height = 26;
      headers.forEach((h, i) => {
        const cell = hRow.getCell(i + 1);
        cell.value = h;
        cell.fill = { type: "pattern", pattern: "solid", fgColor: { argb: "FFE8520A" } };
        cell.font = { bold: true, color: { argb: "FFFFFFFF" }, size: 9 };
        cell.alignment = { horizontal: "center", vertical: "middle" };
        cell.border = { bottom: { style: "medium", color: { argb: "FFCB4A00" } } };
      });

      // Data rows
      displayScores.forEach((e, idx) => {
        const sug = e.score !== null ? ((e.score / 100) * 4 * MULT).toFixed(2) : null;
        const fin = isHistorical ? e.approvedHike : getHike(e.code);
        const finNum = parseFloat(fin) || 0;
        const hikeAmt = fin ? (e.ctc * (finNum / 100)).toFixed(0) : null;
        const newCtc = fin ? (e.ctc + parseFloat(hikeAmt)).toFixed(0) : null;
        const lk = !isHistorical && isLocked(e.code);
        const rowStat = e.score === null ? "Pending" : lk ? "Locked" : (!fin || fin === "") ? "In Progress" : "Hike Set";
        const revSt = getEmpReviewStatus(e.code);
        const bg = idx % 2 === 0 ? "FFFFFFFF" : "FFFAFAFA";

        const row = ws.addRow([
          e.code,
          e.name,
          e.ctc ? "₹" + e.ctc.toLocaleString("en-IN") : "",
          e.score !== null ? parseFloat(e.score.toFixed(2)) : "Awaiting",
          e.score !== null ? parseFloat(((e.score / 100) * 4).toFixed(3)) : "",
          sug ? sug + "%" : "",
          fin ? fin + "%" : "",
          newCtc ? "₹" + parseFloat(newCtc).toLocaleString("en-IN") : "",
          rowStat,
          revSt,
        ]);
        row.height = 18;
        row.eachCell((cell, colIdx) => {
          cell.font = {
            size: 9, color: { argb: colIdx === 1 ? "FFE8520A" : colIdx === 2 ? "FF0D1B2A" : "FF475569" },
            bold: colIdx <= 2
          };
          cell.fill = { type: "pattern", pattern: "solid", fgColor: { argb: bg } };
          cell.border = { bottom: { style: "thin", color: { argb: "FFE5E7EB" } } };
          cell.alignment = { vertical: "middle" };
        });

        // Color-code the Row Status cell
        const statusCell = row.getCell(9);
        const statusColors = { "Locked": { fill: "EDE9FE", font: "6D28D9" }, "Hike Set": { fill: "D1FAE5", font: "065F46" }, "In Progress": { fill: "FEF3C7", font: "92400E" }, "Pending": { fill: "F1F5F9", font: "94A3B8" } };
        const sc = statusColors[rowStat];
        if (sc) {
          statusCell.fill = { type: "pattern", pattern: "solid", fgColor: { argb: "FF" + sc.fill } };
          statusCell.font = { size: 9, bold: true, color: { argb: "FF" + sc.font } };
        }
      });

      // Summary row
      ws.addRow([]);
      const sumRow = ws.addRow(["Total: " + displayScores.length + " employee" + (displayScores.length !== 1 ? "s" : ""), "", "", "", "", "", "", "", "", ""]);
      sumRow.getCell(1).font = { italic: true, size: 9, color: { argb: "FF64748B" } };

      const buf = await wb.xlsx.writeBuffer();
      triggerDownload(new Blob([buf], { type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet" }),
        "Scoring_" + (quarter || "export").replace(/\s/g, "_") + "_" + dateStr + ".xlsx");
      showToast("Scoring data exported to Excel", "#10B981");
    } catch (err) { console.error(err); showToast("Excel export failed", "error"); }
  };

  const exportScoringPDF = async () => {
    if (!displayScores.length) { showToast("No scoring data to export", "error"); return; }
    showToast("Generating PDF…");
    const dateStr = todayIST();
    try {
      const JsPDF = await loadJsPDF();
      const doc = new JsPDF({ orientation: "landscape", unit: "mm", format: "a4" });
      const pageW = 297;

      // Header bar
      doc.setFillColor(232, 82, 10);
      doc.rect(0, 0, pageW, 18, "F");
      doc.setTextColor(255, 255, 255);
      doc.setFontSize(12); doc.setFont("helvetica", "bold");
      doc.text("Dolluz Corp EPR  |  Scoring & Hike Report  —  " + (quarter || ""), 10, 11);
      doc.setFontSize(8); doc.setFont("helvetica", "normal");
      doc.text(displayScores.length + " employee" + (displayScores.length !== 1 ? "s" : "") + "  |  Generated: " + new Date().toLocaleString(), 10, 16);

      const tableRows = displayScores.map((e) => {
        const sug = e.score !== null ? ((e.score / 100) * 4 * MULT).toFixed(2) + "%" : "—";
        const fin = isHistorical ? e.approvedHike : getHike(e.code);
        const lk = !isHistorical && isLocked(e.code);
        const rowStat = e.score === null ? "Pending" : lk ? "Locked" : (!fin || fin === "") ? "In Progress" : "Hike Set";
        return [
          e.code,
          e.name,
          e.ctc ? "₹" + e.ctc.toLocaleString("en-IN") : "—",
          e.score !== null ? e.score.toFixed(2) : "Awaiting",
          e.score !== null ? ((e.score / 100) * 4).toFixed(3) : "—",
          sug,
          fin ? fin + "%" : "—",
          rowStat,
          getEmpReviewStatus(e.code),
        ];
      });

      doc.autoTable({
        startY: 22,
        head: [["Employee ID", "Employee", "CTC (₹/mo)", "Score /100", "Rating /4", "Suggested %", "Approved Hike %", "Row Status", "Review Status"]],
        body: tableRows,
        styles: { fontSize: 8, cellPadding: 2.5, valign: "middle" },
        headStyles: { fillColor: [13, 27, 42], textColor: 255, fontStyle: "bold", fontSize: 8 },
        alternateRowStyles: { fillColor: [248, 250, 252] },
        columnStyles: {
          0: { cellWidth: 18, textColor: [232, 82, 10], fontStyle: "bold" },
          1: { cellWidth: 36, fontStyle: "bold" },
          2: { cellWidth: 22, halign: "right" },
          3: { cellWidth: 22, halign: "center" },
          4: { cellWidth: 20, halign: "center" },
          5: { cellWidth: 22, halign: "center" },
          6: { cellWidth: 26, halign: "center", textColor: [16, 185, 129], fontStyle: "bold" },
          7: { cellWidth: 24, halign: "center" },
          8: { cellWidth: 30, halign: "center" },
        },
        didParseCell: (data) => {
          if (data.section === "body" && data.column.index === 7) {
            const status = data.cell.raw;
            const textColors = {
              "Locked": [109, 40, 217],
              "Hike Set": [6, 95, 70],
              "In Progress": [146, 64, 14],
              "Pending": [148, 163, 184],
            };
            const bgColors = {
              "Locked": [237, 233, 254],
              "Hike Set": [209, 250, 229],
              "In Progress": [254, 243, 199],
              "Pending": [241, 245, 249],
            };
            if (textColors[status]) {
              data.cell.styles.textColor = textColors[status];
              data.cell.styles.fillColor = bgColors[status];
              data.cell.styles.fontStyle = "bold";
            }
          }
        },
        margin: { left: 10, right: 10 },
      });

      // Footer
      const count = doc.getNumberOfPages();
      for (let i = 1; i <= count; i++) {
        doc.setPage(i);
        doc.setTextColor(148, 163, 184); doc.setFontSize(7); doc.setFont("helvetica", "normal");
        doc.text("Page " + i + " of " + count + "  |  Dolluz Corp EPR Portal  |  Confidential", pageW / 2, 207, { align: "center" });
      }

      applyPdfWatermark(doc, await loadWatermarkDataUrl());
      doc.save("Scoring_" + (quarter || "export").replace(/\s/g, "_") + "_" + dateStr + ".pdf");
      showToast("Scoring report exported to PDF", "#10B981");
    } catch (err) { console.error(err); showToast("PDF export failed", "error"); }
  };

  const publishQuarter = async () => {
    // ── Step 1: Persist hike + lock for every row not yet individually locked ──
    // Already-locked rows were saved when the user clicked the lock button —
    // skip them here to avoid redundant API calls.
    const rowsToLock = displayScores.filter(e => {
      const fin = getHike(e.code);
      return e.score !== null && fin && fin !== "" && !isLocked(e.code);
    });

    let apiError = false;
    for (const e of rowsToLock) {
      const hikeValue = getHike(e.code);
      try {
        // Save hike first — the lock API blocks hike updates once scoring_locked=1
        const hikeRes = await apiFetch(`/api/scoring/${e.id}/hike`, {
          method: "PUT",
          body: JSON.stringify({ cycle_id: selCycId, approved_hike: hikeValue }),
        });
        const hikeData = await hikeRes.json();
        if (!hikeData.success) { apiError = true; break; }

        // Then lock the row in DB
        const lockRes = await apiFetch(`/api/scoring/${e.id}/lock`, {
          method: "PUT",
          body: JSON.stringify({ cycle_id: selCycId, lock: true }),
        });
        const lockData = await lockRes.json();
        if (!lockData.success) { apiError = true; break; }
      } catch (_) { apiError = true; break; }
    }

    if (apiError) {
      showToast("Failed to save some hikes — please check rows and retry", "error");
      return;
    }

    // ── Step 2: Update local lock state for all qualifying rows ───────────────
    const lockUpd = {};
    displayScores.forEach(e => {
      const fin = getHike(e.code);
      if (e.score !== null && fin && fin !== "") lockUpd[e.code] = true;
    });
    setScoringLocked(p => ({ ...p, [quarter]: { ...(p[quarter] || {}), ...lockUpd } }));

    // ── Step 3: Bridge — mark all reviews in this quarter as Approved ─────────
    if (setAllReviews) {
      setAllReviews(prev => prev.map(r =>
        r.quarter === quarter ? { ...r, status: "Approved" } : r
      ));
    }

    // ── Step 4: Close the cycle via API ───────────────────────────────────────
    if (selCycle) {
      try {
        const res = await apiFetch(`/api/cycles/${selCycle.id}/close`, { method: "POST" });
        const d = await res.json();
        if (d.success && setCycles) {
          setCycles(prev => prev.map(c => c.id === selCycle.id ? { ...c, status: "Closed", closed: true } : c));
        }
      } catch (e) { }
    }

    setShowPublishModal(false);
    const nHikes = Object.keys(lockUpd).length;
    showToast(quarter + " published — " + nHikes + " hike" + (nHikes !== 1 ? "s" : "") + " finalised & cycle closed", "#10B981");
  };

  // ── Publish readiness ────────────────────────────────────────────────────────
  const scoredEmps = displayScores.filter(e => e.score !== null);
  const hikedEmps = scoredEmps.filter(e => { const h = getHike(e.code); return h && h !== ""; });
  const lockedEmps = scoredEmps.filter(e => isLocked(e.code));
  const allHiked = scoredEmps.length > 0 && hikedEmps.length === scoredEmps.length;
  const allLocked = scoredEmps.length > 0 && lockedEmps.length === scoredEmps.length;
  const publishReady = allHiked;

  // ── Trend ─────────────────────────────────────────────────────────────────────
  // Show the currently loaded cycle scores as a single trend point (historical
  // multi-quarter trend requires fetching scoring data per quarter via API)
  const chronoQtrs = [...allQtrs].reverse();
  const trendData = displayScores.map(e => ({
    name: e.name, code: e.code,
    trend: chronoQtrs.map(q => {
      // Only the currently selected cycle has loaded score data
      const entry = (quarter && q === quarter) ? empScores.find(x => x.code === e.code) : null;
      const hikeVal = entry ? ((scoringHikes[quarter] || {})[e.code] || entry.approvedHike) : null;
      return {
        q: (q || "").split(" ")[0] + " '" + ((q || "").split(" ")[1] || "").slice(2),
        score: entry ? entry.score : null,
        hike: hikeVal,
      };
    })
  }));

  // ── Scoring Guide modal ──────────────────────────────────────────────────────
  const ScoringGuide = () => {
    const step = GUIDE_STEPS[guideStep];
    return (
      <div className="modal-overlay" style={{ left: 236 }}>
        <div className="modal" style={{ maxWidth: 560, width: "min(560px, calc(100% - 32px))" }}>
          <div style={{ borderRadius: "14px 14px 0 0", overflow: "hidden", overflowX: "auto" }}>
            <div style={{ background: "linear-gradient(135deg," + step.color + "ee," + step.color + "99)", padding: "28px 28px 20px" }}>
              <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: 12 }}>
                <div style={{ display: "flex", gap: 6 }}>
                  {GUIDE_STEPS.map((_, i) => (
                    <div key={i} onClick={() => setGuideStep(i)} style={{
                      width: i === guideStep ? 24 : 8, height: 8, borderRadius: 4,
                      background: i === guideStep ? "#fff" : "rgba(255,255,255,.4)", cursor: "pointer", transition: "width 0.2s"
                    }} />
                  ))}
                </div>
                <button onClick={() => setShowGuide(false)} style={{ background: "rgba(255,255,255,.2)", border: "none", borderRadius: 8, color: "#fff", padding: "4px 10px", fontSize: 16, cursor: "pointer" }}>&#215;</button>
              </div>
              <div style={{ fontSize: 38, marginBottom: 10 }}>{step.icon}</div>
              <div style={{ fontFamily: "'Sora',sans-serif", fontWeight: 800, fontSize: 19, color: "#fff", marginBottom: 4 }}>{step.title}</div>
              <div style={{ fontSize: 11, color: "rgba(255,255,255,.7)", fontWeight: 600 }}>Step {guideStep + 1} of {GUIDE_STEPS.length}</div>
            </div>
          </div>
          <div style={{ padding: "22px 26px" }}>
            <p style={{ fontSize: 14, color: "#334155", lineHeight: 1.8, marginBottom: 14 }}>{step.body}</p>
            <div style={{ background: "#FFFBEB", border: "1px solid #FDE68A", borderRadius: 10, padding: "11px 15px", display: "flex", gap: 10 }}>
              <span style={{ fontSize: 16, flexShrink: 0 }}>&#128161;</span>
              <p style={{ fontSize: 13, color: "#78350F", margin: 0, lineHeight: 1.7 }}>{step.tip}</p>
            </div>
          </div>
          <div style={{ padding: "0 26px 22px", display: "flex", justifyContent: "space-between", alignItems: "center" }}>
            <button onClick={() => setGuideStep(s => s - 1)}
              style={{ opacity: guideStep === 0 ? 0.4 : 1, fontSize: 13, padding: "8px 16px", background: "#F1F5F9", border: "1px solid #E2E8F0", borderRadius: 8, cursor: guideStep === 0 ? "default" : "pointer", color: "#475569" }}>
              &#8592; Previous
            </button>
            <span style={{ fontSize: 11, color: "#94A3B8" }}>Click dots to jump</span>
            {guideStep < GUIDE_STEPS.length - 1
              ? <button className="btn-primary" style={{ fontSize: 13 }} onClick={() => setGuideStep(s => s + 1)}>Next &#8594;</button>
              : <button className="btn-primary" style={{ fontSize: 13, background: "#10B981" }} onClick={() => setShowGuide(false)}>Got it &#10003;</button>}
          </div>
        </div>
      </div>
    );
  };

  return (
    <div className="fade-in">
      <TopBar title="Scoring & Hike Calculation" subtitle="Internal only — weighted competency scores and hike percentages" {...topBarProps} />

      {/* ── Guide banner ── */}
      <div style={{
        background: "linear-gradient(135deg,#0D1B2A,#1E3A5F)", borderRadius: 14, padding: "16px 22px", marginBottom: 18,
        display: "flex", alignItems: "center", gap: 14
      }}>
        <div style={{
          width: 40, height: 40, borderRadius: 10, background: "rgba(232,82,10,.25)", flexShrink: 0,
          display: "flex", alignItems: "center", justifyContent: "center", fontSize: 20
        }}>&#127891;</div>
        <div style={{ flex: 1 }}>
          <div style={{ fontFamily: "'Sora',sans-serif", fontWeight: 700, fontSize: 13, color: "#fff", marginBottom: 2 }}>New to Scoring & Hikes?</div>
          <div style={{ fontSize: 11, color: "rgba(255,255,255,.5)" }}>6-step walkthrough: weights → scores → hike formula → lock → publish</div>
        </div>
        <button onClick={() => { setGuideStep(0); setShowGuide(true); }}
          style={{ background: "#E8520A", border: "none", borderRadius: 9, color: "#fff", padding: "9px 18px", fontSize: 12, fontWeight: 700, cursor: "pointer", flexShrink: 0 }}>
          &#9654; Open Guide
        </button>
      </div>

      {/* ── Internal only banner ── */}
      <div style={{ background: "#FFF5F0", border: "1.5px solid #FED7C3", borderRadius: 10, padding: "10px 16px", marginBottom: 18, display: "flex", alignItems: "center", gap: 10 }}>
        <span style={{ fontSize: 16 }}>&#128274;</span>
        <span style={{ fontSize: 13, color: "#92400E", fontWeight: 500 }}>
          <strong>Internal Only:</strong> Scores and hike percentages are never visible to stakeholders or employees.
        </span>
      </div>

      {/* ── Cycle selector + toolbar ── */}
      <div style={{ display: "flex", alignItems: "center", gap: 10, marginBottom: 20, flexWrap: "wrap" }}>
        <span style={{ fontSize: 12, fontWeight: 600, color: "#64748B" }}>Cycle:</span>
        <select value={selCycId} onChange={e => setSelCycId(e.target.value)}
          style={{
            padding: "7px 13px", borderRadius: 9, border: "1.5px solid #E2E8F0", fontSize: 13,
            fontWeight: 600, color: "#0D1B2A", background: "#fff", cursor: "pointer", minWidth: 170
          }}>
          {scoringCycles.map(c => (
            <option key={c.id} value={c.id}>{c.q} {c.status === "Active" ? "— Active" : "— Closed"}</option>
          ))}
        </select>
        {selCycle && (
          <span style={{
            fontSize: 11, background: selCycle.status === "Active" ? "#D1FAE5" : "#F1F5F9",
            color: selCycle.status === "Active" ? "#065F46" : "#64748B",
            border: "1px solid " + (selCycle.status === "Active" ? "#A7F3D0" : "#E2E8F0"),
            borderRadius: 100, padding: "3px 10px", fontWeight: 700
          }}>
            {selCycle.status === "Active" ? "LIVE" : "CLOSED"}
          </span>
        )}
        {isHistorical && (
          <span style={{ fontSize: 11, color: "#64748B", background: "#F1F5F9", border: "1px solid #E2E8F0", borderRadius: 100, padding: "3px 10px" }}>
            &#128193; Read-only
          </span>
        )}
        <div style={{ marginLeft: "auto", display: "flex", gap: 8, flexWrap: "wrap" }}>
          <button onClick={() => { setGuideStep(0); setShowGuide(true); }}
            style={{
              padding: "7px 13px", borderRadius: 8, fontSize: 12, fontWeight: 600, cursor: "pointer",
              border: "1.5px solid #E2E8F0", background: "#fff", color: "#475569"
            }}>
            &#127891; How-to Guide
          </button>
          <button onClick={() => setShowTrend(t => !t)}
            style={{
              padding: "7px 13px", borderRadius: 8, fontSize: 12, fontWeight: 600, cursor: "pointer",
              border: "1.5px solid " + (showTrend ? "#8B5CF6" : "#E2E8F0"),
              background: showTrend ? "#F5F3FF" : "#fff", color: showTrend ? "#7C3AED" : "#475569"
            }}>
            &#128200; {showTrend ? "Hide" : "Show"} Trends
          </button>
        </div>
      </div>

      {/* ── Trend panel ── */}
      {showTrend && (
        <div className="card" style={{ padding: "20px 24px", marginBottom: 18, overflow: "hidden", overflowX: "auto" }}>
          <div style={{ fontFamily: "'Sora',sans-serif", fontWeight: 700, fontSize: 14, color: "#0D1B2A", marginBottom: 4 }}>Employee Score Trend — All Quarters</div>
          <div style={{ fontSize: 12, color: "#64748B", marginBottom: 14 }}>Score /100 · green = hike approved</div>
          <div style={{ overflowX: "auto" }}>
            <table style={{ minWidth: 700 }}>
              <thead>
                <tr>
                  <th style={{ textAlign: "left" }}>Employee</th>
                  {chronoQtrs.map(q => <th key={q} style={{ textAlign: "center", fontFamily: "'JetBrains Mono',monospace", fontSize: 11 }}>{q.replace(" 20", " '")}</th>)}
                  <th style={{ textAlign: "center" }}>Trend</th>
                </tr>
              </thead>
              <tbody>
                {trendData.map(emp => {
                  const scores = emp.trend.map(t => t.score).filter(s => s !== null);
                  const delta = scores.length >= 2 ? (scores[scores.length - 1] - scores[0]).toFixed(1) : null;
                  return (
                    <tr key={emp.code}>
                      <td>
                        <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
                          <Avatar name={emp.name} size={26} />
                          <div>
                            <div style={{ fontWeight: 600, fontSize: 12 }}>{emp.name}</div>
                            <div style={{ fontSize: 10, color: "#94A3B8" }}>{emp.code}</div>
                          </div>
                        </div>
                      </td>
                      {emp.trend.map((t, ti) => (
                        <td key={ti} style={{ textAlign: "center" }}>
                          {t.score !== null
                            ? <div>
                              <div style={{ fontFamily: "'JetBrains Mono',monospace", fontWeight: 700, fontSize: 12, color: scoreColor(t.score) }}>
                                {t.score.toFixed(1)}
                              </div>
                              {t.hike && <div style={{ fontSize: 9, color: "#10B981", fontWeight: 600 }}>{t.hike}%</div>}
                            </div>
                            : <span style={{ color: "#CBD5E1" }}>—</span>}
                        </td>
                      ))}
                      <td style={{ textAlign: "center" }}>
                        {delta !== null
                          ? <span style={{ fontWeight: 700, fontSize: 12, color: parseFloat(delta) >= 0 ? "#10B981" : "#EF4444" }}>
                            {parseFloat(delta) >= 0 ? "▲" : "▼"} {Math.abs(parseFloat(delta))}
                          </span>
                          : <span style={{ color: "#94A3B8" }}>—</span>}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        </div>
      )}

      <div style={{ display: "grid", gridTemplateColumns: "290px 1fr", gap: 16 }}>

        {/* ── Competency weights ── */}
        <div className="card" style={{ padding: "18px 20px", alignSelf: "start" }}>
          <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 4 }}>
            <div style={{ fontFamily: "'Sora',sans-serif", fontWeight: 700, fontSize: 14, color: "#0D1B2A" }}>Competency Weights</div>
            <span style={{
              fontSize: 10, color: isHistorical ? "#94A3B8" : "#10B981",
              background: isHistorical ? "#F1F5F9" : "#F0FDF4", padding: "2px 8px", borderRadius: 100
            }}>
              {isHistorical ? "Locked" : "Editable"}
            </span>
          </div>
          <div style={{ fontSize: 11, color: "#64748B", marginBottom: 14 }}>{quarter} · {isHistorical ? "Applied weights" : "Adjust and save"}</div>
          {comps.map((c, i) => (
            <div key={i} style={{ marginBottom: 12 }}>
              <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 4 }}>
                <span style={{ fontSize: 12, color: "#475569", fontWeight: 600 }}>{c.name}</span>
                <div style={{ display: "flex", alignItems: "center", gap: 5 }}>
                  <input type="number" min={0} max={100} value={c.weight} onChange={e => updW(i, e.target.value)} disabled={isHistorical}
                    onKeyDown={(e) => { if (["-", "+", "e", "E"].includes(e.key)) e.preventDefault(); }}
                    style={{
                      width: 52, padding: "3px 7px", fontSize: 12, fontWeight: 700, textAlign: "center",
                      color: isHistorical ? "#94A3B8" : "#E8520A",
                      border: "1.5px solid " + (isHistorical ? "#E2E8F0" : "#FED7C3"),
                      borderRadius: 7, background: isHistorical ? "#F8FAFC" : "#fff"
                    }} />
                  <span style={{ fontSize: 11, color: "#94A3B8" }}>%</span>
                </div>
              </div>
              <input type="range" min={0} max={50} value={c.weight} onChange={e => updW(i, e.target.value)}
                disabled={isHistorical} style={{ width: "100%", accentColor: isHistorical ? "#CBD5E1" : "#E8520A", cursor: isHistorical ? "default" : "pointer" }} />
            </div>
          ))}
          <div style={{ borderTop: "1px solid #F1F5F9", paddingTop: 10, display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 8 }}>
            <span style={{ fontSize: 12, fontWeight: 600, color: "#475569" }}>Total</span>
            <span style={{ fontSize: 15, fontWeight: 800, color: weightOk ? "#10B981" : "#EF4444" }}>{totalW}%</span>
          </div>
          {!isHistorical && (
            <>
              {!weightOk && <div style={{ fontSize: 11, color: "#EF4444", background: "#FEF2F2", padding: "7px 10px", borderRadius: 7, marginBottom: 8 }}>Weights must sum to exactly 100%.</div>}
              <button className="btn-primary" style={{
                width: "100%", justifyContent: "center", fontSize: 13,
                ...(!weightOk ? { background: "#E2E8F0", color: "#94A3B8", cursor: "not-allowed", boxShadow: "none", transform: "none" } : {})
              }}
                onClick={saveW}>
                {dirty ? "Save Weights" : <span>&#10003; Weights Saved</span>}
              </button>
            </>
          )}
          <div style={{ marginTop: 10, fontSize: 11, color: "#94A3B8", textAlign: "center" }}>
            Multiplier: <strong style={{ color: "#E8520A" }}>{MULT}x</strong> · Max hike: <strong style={{ color: "#E8520A" }}>{4 * MULT}%</strong>
          </div>
        </div>

        {/* ── Results table ── */}
        <div className="card" style={{ overflow: "hidden", overflowX: "auto" }}>
          <div style={{ padding: "14px 18px", borderBottom: "1px solid #F1F5F9", display: "flex", justifyContent: "space-between", alignItems: "center", flexWrap: "wrap", gap: 10 }}>
            <div>
              <div style={{ fontFamily: "'Sora',sans-serif", fontWeight: 700, fontSize: 14, color: "#0D1B2A" }}>
                {isHistorical ? "Closed Appraisal Record" : "Appraisal Results"} — {quarter}
              </div>
              {!isHistorical && (
                <div style={{ fontSize: 11, color: "#64748B", marginTop: 2 }}>
                  {lockedEmps.length}/{scoredEmps.length} locked · {hikedEmps.length}/{scoredEmps.length} hike{hikedEmps.length !== 1 ? "s" : ""} set
                </div>
              )}
            </div>
            <div style={{ display: "flex", gap: 8, flexWrap: "wrap" }}>
              {!isHistorical && (
                <>
                  <button className="btn-secondary" style={{ fontSize: 12 }} onClick={bulkApprove}>&#9989; Bulk Apply Suggested</button>
                  <button onClick={() => setShowPublishModal(true)}
                    style={{
                      padding: "7px 14px", borderRadius: 9, fontSize: 12, fontWeight: 700, cursor: publishReady ? "pointer" : "not-allowed",
                      border: "1.5px solid " + (publishReady ? "#10B981" : "#E2E8F0"),
                      background: publishReady ? "#10B981" : "#F8FAFC",
                      color: publishReady ? "#fff" : "#94A3B8",
                      opacity: publishReady ? 1 : 0.6
                    }}
                    disabled={!publishReady}>
                    &#128640; Publish Quarter
                  </button>
                </>
              )}
              <button className="btn-secondary" style={{ fontSize: 12, color: "#10B981", borderColor: "#A7F3D0" }}
                onMouseEnter={e => { e.currentTarget.style.borderColor = "#10B981"; e.currentTarget.style.background = "#F0FDF4"; }}
                onMouseLeave={e => { e.currentTarget.style.borderColor = "#A7F3D0"; e.currentTarget.style.background = "#fff"; }}
                onClick={exportScoringXLS}>&#128202; Export XLS</button>
              <button className="btn-secondary" style={{ fontSize: 12, color: "#EF4444", borderColor: "#FECACA" }}
                onMouseEnter={e => { e.currentTarget.style.borderColor = "#EF4444"; e.currentTarget.style.background = "#FEF2F2"; }}
                onMouseLeave={e => { e.currentTarget.style.borderColor = "#FECACA"; e.currentTarget.style.background = "#fff"; }}
                onClick={exportScoringPDF}>&#128196; Export PDF</button>
            </div>
          </div>

          {/* Progress bar for active quarter */}
          {!isHistorical && scoredEmps.length > 0 && (
            <div style={{ padding: "10px 18px", background: "#F8FAFC", borderBottom: "1px solid #F1F5F9", display: "flex", alignItems: "center", gap: 12 }}>
              <span style={{ fontSize: 11, fontWeight: 600, color: "#64748B", whiteSpace: "nowrap" }}>Publish progress:</span>
              <div style={{ flex: 1, background: "#E2E8F0", borderRadius: 100, height: 8, overflow: "hidden" }}>
                <div style={{
                  height: "100%", borderRadius: 100, background: "#10B981",
                  width: (hikedEmps.length / scoredEmps.length * 100) + "%", transition: "width .4s"
                }} />
              </div>
              <span style={{ fontSize: 11, fontWeight: 700, color: "#10B981", whiteSpace: "nowrap" }}>
                {Math.round(hikedEmps.length / scoredEmps.length * 100)}%
              </span>
              {!publishReady && (
                <span style={{ fontSize: 11, color: "#F59E0B", fontWeight: 600 }}>
                  &#9888; {scoredEmps.length - hikedEmps.length} hike{scoredEmps.length - hikedEmps.length !== 1 ? "s" : ""} missing
                </span>
              )}
            </div>
          )}

          <div
            style={{ overflowX: "auto" }}
            onWheel={e => {
              // Shift+wheel → horizontal scroll. Browsers don't reliably route
              // shift+wheel events to nested overflow containers on Windows,
              // so we forward deltaY → scrollLeft manually.
              if (e.shiftKey && e.deltaY !== 0) {
                e.currentTarget.scrollLeft += e.deltaY;
                e.preventDefault();
              }
            }}>
            <table style={{ minWidth: isHistorical ? 900 : 1140 }}>
              <thead>
                <tr>
                  <th>Employee</th>
                  <th>Review Status</th>
                  <th>CTC (₹/mo)</th>
                  <th>Score /100</th>
                  <th>Rating /4</th>
                  <th>Suggested %</th>
                  {isHistorical
                    ? <th>Approved Hike %</th>
                    : <><th>Hike %</th><th>New CTC</th><th>Row Status</th><th>Actions</th></>
                  }
                </tr>
              </thead>
              <tbody>
                {scoringLoading && <tr><td colSpan={10} style={{ textAlign: "center", color: "#94A3B8", padding: 24 }}>Loading scores...</td></tr>}
                {!scoringLoading && displayScores.length === 0 && <tr><td colSpan={10} style={{ textAlign: "center", color: "#94A3B8", padding: 24 }}>No scoring data for this cycle yet.</td></tr>}
                {displayScores.map(e => {
                  const sug = e.score !== null ? ((e.score / 100) * 4 * MULT).toFixed(2) : null;
                  const fin = isHistorical ? e.approvedHike : getHike(e.code);
                  const finNum = parseFloat(fin) || 0;
                  const hikeAmt = fin ? (e.ctc * (finNum / 100)).toFixed(0) : null;
                  const newCtc = fin ? (e.ctc + parseFloat(hikeAmt)).toFixed(0) : null;
                  const lk = !isHistorical && isLocked(e.code);
                  const rowStat = e.score === null ? "Pending" : lk ? "Locked" : (!fin || fin === "") ? "In Progress" : "Hike Set";
                  const revSt = getEmpReviewStatus(e.code);
                  const canScore = revSt === "Submitted" || revSt === "Closed";

                  return (
                    <tr key={e.code} style={{ background: lk ? "#FAFBFF" : "#fff", opacity: (!canScore && !isHistorical && e.score === null) ? 0.7 : 1 }}>
                      <td style={{ whiteSpace: "nowrap" }}>
                        <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
                          <Avatar name={e.name} size={28} />
                          <div style={{ minWidth: 0 }}>
                            <div style={{ fontWeight: 600, fontSize: 13, whiteSpace: "nowrap" }}>{e.name}</div>
                            <div style={{ fontSize: 10, color: "#94A3B8", fontFamily: "'JetBrains Mono',monospace", whiteSpace: "nowrap" }}>{e.code}</div>
                          </div>
                        </div>
                      </td>
                      <td><RevBadge status={revSt} /></td>
                      <td style={{ fontFamily: "'JetBrains Mono',monospace", fontSize: 12, fontWeight: 600, color: "#475569", whiteSpace: "nowrap" }}>₹{e.ctc.toLocaleString("en-IN")}</td>
                      <td>
                        {e.score !== null
                          ? <span style={{
                            fontFamily: "'JetBrains Mono',monospace", fontWeight: 800, fontSize: 13,
                            background: scoreBg(e.score), color: scoreColor(e.score),
                            border: "1px solid " + scoreBdr(e.score), borderRadius: 8,
                            padding: "3px 9px", display: "inline-block"
                          }}>
                            {e.score.toFixed(2)}
                          </span>
                          : <span style={{ color: "#94A3B8", fontSize: 12 }}>Awaiting</span>}
                      </td>
                      <td style={{ fontFamily: "'JetBrains Mono',monospace", fontWeight: 700, color: e.score ? scoreColor(e.score) : "#94A3B8", fontSize: 12 }}>
                        {e.score !== null ? ((e.score / 100) * 4).toFixed(3) : "—"}
                      </td>
                      <td>
                        {sug
                          ? <span style={{ fontWeight: 700, color: "#E8520A", fontSize: 13 }}>{sug}%</span>
                          : <span style={{ color: "#94A3B8", fontSize: 12 }}>—</span>}
                      </td>

                      {isHistorical ? (
                        <td>
                          {e.approvedHike
                            ? <span style={{ fontFamily: "'JetBrains Mono',monospace", fontWeight: 800, fontSize: 14, color: "#10B981" }}>{e.approvedHike}%</span>
                            : <span style={{ color: "#94A3B8" }}>—</span>}
                        </td>
                      ) : (
                        <>
                          <td>
                            {e.score !== null
                              ? <div style={{ display: "flex", alignItems: "center", gap: 5 }}>
                                <input style={{
                                  width: 64, padding: "5px 7px", fontSize: 13, fontWeight: 700, textAlign: "center", borderRadius: 8,
                                  color: lk ? "#8B5CF6" : "#10B981", background: lk ? "#F5F3FF" : "#fff",
                                  borderColor: lk ? "#DDD6FE" : "#E2E8F0", border: "1.5px solid"
                                }}
                                  value={fin || ""} onChange={ev => !lk && setHike(e.code, ev.target.value)} disabled={lk} placeholder="—" />
                                <span style={{ fontSize: 12, color: "#64748B" }}>%</span>
                                {!lk && (
                                  <button
                                    title={fin && fin !== "" ? "Confirm hike: " + fin + "%" : (sug ? "Apply suggested: " + sug + "%" : "")}
                                    onClick={() => {
                                      // If user typed a custom value → keep it (do nothing, it's already in state)
                                      // If field is empty → fill with suggested value
                                      if (!fin || fin === "") applySuggested(e.code, e.score);
                                    }}
                                    style={{
                                      background: fin && fin !== "" ? "#F0FDF4" : "#FFF7ED",
                                      border: "1px solid " + (fin && fin !== "" ? "#BBF7D0" : "#FED7AA"),
                                      borderRadius: 6,
                                      color: fin && fin !== "" ? "#10B981" : "#EA580C",
                                      fontSize: 12, fontWeight: 700, padding: "4px 8px", cursor: "pointer"
                                    }}>
                                    &#10003;
                                  </button>
                                )}
                              </div>
                              : <span style={{ color: "#94A3B8", fontSize: 12 }}>—</span>}
                          </td>
                          <td style={{ fontFamily: "'JetBrains Mono',monospace", fontSize: 12 }}>
                            {newCtc
                              ? <div>
                                <div style={{ fontWeight: 700, color: "#10B981", whiteSpace: "nowrap" }}>₹{parseFloat(newCtc).toLocaleString("en-IN")}</div>
                                <div style={{ fontSize: 10, color: "#94A3B8", whiteSpace: "nowrap" }}>+₹{parseFloat(hikeAmt).toLocaleString("en-IN")}/mo</div>
                              </div>
                              : <span style={{ color: "#94A3B8" }}>—</span>}
                          </td>
                          <td>
                            <span style={{
                              fontSize: 11, fontWeight: 700, padding: "3px 9px", borderRadius: 100,
                              background: rowStat === "Locked" ? "#EDE9FE" : rowStat === "Hike Set" ? "#D1FAE5" : rowStat === "In Progress" ? "#FEF3C7" : "#F1F5F9",
                              color: rowStat === "Locked" ? "#6D28D9" : rowStat === "Hike Set" ? "#065F46" : rowStat === "In Progress" ? "#92400E" : "#94A3B8",
                              border: "1px solid " + (rowStat === "Locked" ? "#DDD6FE" : rowStat === "Hike Set" ? "#A7F3D0" : rowStat === "In Progress" ? "#FDE68A" : "#E2E8F0")
                            }}>
                              {rowStat}
                            </span>
                          </td>
                          <td style={{ whiteSpace: "nowrap" }}>
                            <div style={{ display: "flex", gap: 6, flexWrap: "nowrap", alignItems: "center" }}>
                              {/* Breakdown — always available so admin can see why a score is what it is */}
                              <button onClick={() => openBreakdown(e)}
                                title="View stakeholder responses, score calculation, and hike formula"
                                style={{
                                  background: "#EFF6FF", color: "#2563EB",
                                  border: "1.5px solid #BFDBFE", borderRadius: 7,
                                  padding: "5px 10px", fontSize: 11, fontWeight: 700, cursor: "pointer",
                                  display: "inline-flex", alignItems: "center", gap: 4, whiteSpace: "nowrap",
                                }}>
                                👁 Breakdown
                              </button>
                              {e.score !== null && (
                                <button onClick={() => toggleLk(e.id, e.code)}
                                  style={{
                                    background: lk ? "#EDE9FE" : "#F1F5F9", color: lk ? "#6D28D9" : "#64748B",
                                    border: "1.5px solid " + (lk ? "#DDD6FE" : "#E2E8F0"), borderRadius: 7,
                                    padding: "5px 10px", fontSize: 11, fontWeight: 700, cursor: "pointer", whiteSpace: "nowrap",
                                  }}>
                                  {lk ? <span>&#128274; Locked</span> : "Lock"}
                                </button>
                              )}
                            </div>
                          </td>
                        </>
                      )}
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        </div>
      </div>

      {/* ── Publish Confirm Modal ── */}
      {showPublishModal && (
        <div className="modal-overlay" style={{ left: 236 }}>
          <div className="modal" style={{ maxWidth: 440, width: "min(440px, calc(100% - 32px))" }}>
            <div style={{
              padding: "22px 26px", background: "linear-gradient(135deg,#065F46,#10B981)", borderRadius: "14px 14px 0 0",
              display: "flex", justifyContent: "space-between", alignItems: "center"
            }}>
              <div>
                <div style={{ fontFamily: "'Sora',sans-serif", fontWeight: 800, fontSize: 16, color: "#fff" }}>Publish Quarter</div>
                <div style={{ fontSize: 12, color: "rgba(255,255,255,.7)", marginTop: 2 }}>{quarter}</div>
              </div>
              <button onClick={() => setShowPublishModal(false)} style={{ background: "rgba(255,255,255,.2)", border: "none", borderRadius: 8, color: "#fff", padding: "5px 11px", fontSize: 18, cursor: "pointer" }}>&#215;</button>
            </div>
            <div style={{ padding: "22px 26px" }}>
              <div style={{ fontSize: 14, color: "#0D1B2A", lineHeight: 1.8, marginBottom: 16 }}>
                This will <strong>lock all {hikedEmps.length} hike row{hikedEmps.length !== 1 ? "s" : ""}</strong> and mark them as finalised.
              </div>
              <div style={{ background: "#FFFBEB", border: "1px solid #FDE68A", borderRadius: 9, padding: "11px 14px", fontSize: 13, color: "#92400E" }}>
                &#9888; This action cannot be undone. Make sure all hike values are correct before publishing.
              </div>
              {!allLocked && (
                <div style={{ background: "#EFF6FF", border: "1px solid #BFDBFE", borderRadius: 9, padding: "11px 14px", fontSize: 13, color: "#1E40AF", marginTop: 10 }}>
                  &#8505; {scoredEmps.length - lockedEmps.length} row{scoredEmps.length - lockedEmps.length !== 1 ? "s" : ""} not yet individually locked — publishing will lock them all.
                </div>
              )}
            </div>
            <div style={{ padding: "0 26px 22px", display: "flex", gap: 10 }}>
              <button className="btn-ghost" style={{ flex: 1, justifyContent: "center" }} onClick={() => setShowPublishModal(false)}>Cancel</button>
              <button onClick={publishQuarter}
                style={{
                  flex: 1, background: "#10B981", color: "#fff", border: "none", borderRadius: 9,
                  padding: "10px 0", fontSize: 13, fontWeight: 700, cursor: "pointer"
                }}>
                &#128640; Publish Now
              </button>
            </div>
          </div>
        </div>
      )}

      {toast && <Toast msg={toast} type={toastType} />}
      {showGuide && <ScoringGuide />}

      {/* ── Per-employee Scoring Breakdown drawer ─────────────────────── */}
      {breakdownEmp && (
        <BreakdownDrawer
          emp={breakdownEmp}
          data={breakdownData}
          loading={breakdownLoading}
          error={breakdownError}
          onClose={closeBreakdown}
          MULT={MULT}
          comps={comps}
        />
      )}
    </div>
  );
};

// ═══════════════════════════════════════════════════════════════════════════
//  Per-employee Scoring Breakdown drawer
//  Slides in from the right; shows three stacked sections:
//   1. Stakeholder responses (one card per reviewer with per-criterion ratings
//      grouped by section + their text feedback)
//   2. Score calculation (per-criterion aggregate across all stakeholders →
//      normalized to 0-100 → final score)
//   3. Hike breakdown (formula steps + CTC impact)
// ═══════════════════════════════════════════════════════════════════════════
function BreakdownDrawer({ emp, data, loading, error, onClose, MULT, comps }) {
  const [tab, setTab] = useState(0);
  const [exporting, setExporting] = useState(false);
  const ratingLabel = r => (["—", "Needs Work", "Below Avg", "Meets Expectations", "Exceeds Expectations"])[r] || "—";
  const ratingColor = r => (["#94A3B8", "#EF4444", "#F59E0B", "#3B82F6", "#10B981"])[r] || "#94A3B8";
  const scoreColor = s => s == null ? "#94A3B8" : s >= 85 ? "#10B981" : s >= 70 ? "#E8520A" : "#EF4444";
  const fmtDateTime = v => v ? new Date(v).toLocaleString("en-IN", { day: "2-digit", month: "short", year: "numeric", hour: "2-digit", minute: "2-digit" }) : "—";

  const TABS = ["Stakeholder Responses", "Score Calculation", "Hike Breakdown", "Radar View"];

  const submittedReviews = (data && data.stakeholder_responses || []).filter(r => r.status === "Submitted" || r.status === "Approved");

  // ── Per-competency aggregator (replaces the prior per-criterion table) ──
  // The `competencies` table holds the 6 named competencies + their weights.
  // The `review_criteria` table holds 17+ criteria grouped into broader
  // sections ("Performance", "Interpersonal", "Values", "Conduct"). The
  // section names do NOT match competency names — instead, six specific
  // criteria are *named* exactly like the competencies (e.g. competency
  // "Communication" ↔ criterion labelled "Communication" inside the
  // "Interpersonal" section). So the safest mapping is: match by criterion
  // label = competency name (case-insensitive). Criteria that don't match
  // any competency are still part of the backend's overall average score
  // but aren't surfaced in this competency-level breakdown.
  const perCompetency = (() => {
    if (!data || !comps) return [];
    return (comps || []).map(comp => {
      const compName = String(comp.name || "").trim().toLowerCase();
      const matchingCriteria = data.criteria.filter(c => String(c.label || "").trim().toLowerCase() === compName);
      const allScores = [];
      for (const r of submittedReviews) {
        for (const c of matchingCriteria) {
          const cs = r.criteria_scores.find(x => x.criterion_id === c.id);
          if (cs && cs.score != null) allScores.push(cs.score);
        }
      }
      const avg = allScores.length > 0 ? allScores.reduce((s, x) => s + x, 0) / allScores.length : null;
      const normalized = avg != null ? avg * 25 : null;
      const weighted = normalized != null ? (normalized * Number(comp.weight || 0)) / 100 : null;
      return {
        id: comp.id,
        name: comp.name,
        weight: Number(comp.weight || 0),
        criteria_count: matchingCriteria.length,
        n: allScores.length,
        avg,
        normalized,
        weighted,
      };
    });
  })();
  const competencyWeightedSum = perCompetency.reduce((s, c) => s + (c.weighted || 0), 0);

  // Stakeholder weight distribution — in this app every submitted reviewer
  // contributes equally to the final score (the backend averages all
  // criterion scores across all submitted reviews). We surface that as
  // equal slices so the admin can see who counted.
  const stakeholderSlices = submittedReviews.length > 0
    ? submittedReviews.map(r => ({
      id: r.stakeholder_id,
      name: r.stakeholder_name,
      level: r.stakeholder_level,
      weightPct: parseFloat((100 / submittedReviews.length).toFixed(2)),
    }))
    : [];

  // ── Export helpers ─────────────────────────────────────────────────
  const exportExcel = async () => {
    if (!data) return;
    setExporting(true);
    try {
      const ExcelJS = await loadExcelJS();
      const wb = new ExcelJS.Workbook();

      // ── Standard Dolluz EPR Excel palette (mirrors the employee /
      // client export sheets so all our exports look the same) ───
      const C = {
        orange: "FFE8520A",   // brand / required
        darkNavy: "FF0D1B2A",   // section banners
        mediumNavy: "FF1E3A5F",   // table header
        slate: "FF374151",   // body text
        white: "FFFFFFFF",
        altRow: "FFFAFAFA",   // zebra-stripe alt rows
        cream: "FFFFF3E0",   // info / text-response sub-header
        creamFont: "FFB45309",
        lightGrey: "FFF1F5F9",
        borderLight: "FFE5E7EB",
        borderMid: "FFD1D5DB",
        green: "FF10B981",
        greenBg: "FFD1FAE5",
      };
      const thinBorder = {
        top: { style: "thin", color: { argb: C.borderLight } },
        left: { style: "thin", color: { argb: C.borderLight } },
        bottom: { style: "thin", color: { argb: C.borderLight } },
        right: { style: "thin", color: { argb: C.borderLight } },
      };
      const ratingLbl = s => s == null ? "—" : (["—", "Needs Work", "Below Avg", "Meets Expectations", "Exceeds Expectations"])[s] || "—";

      // Reusable styled-row helpers
      const writeBanner = (ws, text, spanCols) => {
        const row = ws.addRow([text]);
        row.height = 24;
        const c = row.getCell(1);
        c.fill = { type: "pattern", pattern: "solid", fgColor: { argb: C.darkNavy } };
        c.font = { bold: true, color: { argb: C.white }, size: 11 };
        c.alignment = { vertical: "middle", indent: 1 };
        ws.mergeCells(`A${row.number}:${String.fromCharCode(64 + spanCols)}${row.number}`);
        return row;
      };
      const writeHeader = (ws, cols, fill = C.mediumNavy) => {
        const row = ws.addRow(cols);
        row.height = 22;
        row.eachCell(cell => {
          cell.fill = { type: "pattern", pattern: "solid", fgColor: { argb: fill } };
          cell.font = { bold: true, color: { argb: C.white }, size: 10 };
          cell.alignment = { vertical: "middle", horizontal: "center", wrapText: true };
          cell.border = thinBorder;
        });
        return row;
      };
      const styleBodyRow = (row, { isAlt = false, fillOverride = null, textColor = C.slate, bold = false } = {}) => {
        row.eachCell({ includeEmpty: true }, cell => {
          cell.fill = { type: "pattern", pattern: "solid", fgColor: { argb: fillOverride || (isAlt ? C.altRow : C.white) } };
          cell.font = { size: 10, color: { argb: textColor }, bold };
          cell.alignment = { vertical: "middle", wrapText: cell.col === 5 };
          cell.border = thinBorder;
        });
      };

      // ════════════════════════════════════════════════════════════════
      // ── SHEET 1: Summary (first) ─────────────────────────────────
      const wsSummary = wb.addWorksheet("Summary");
      wsSummary.columns = [{ width: 28 }, { width: 38 }];
      writeBanner(wsSummary, "Scoring Breakdown Summary", 2);
      writeHeader(wsSummary, ["Field", "Value"]);
      const summaryRows = [
        ["Employee", data.employee.name],
        ["Employee ID", data.employee.code],
        ["Designation", data.employee.designation || "—"],
        ["Cycle", data.cycle.quarter_label],
        ["Final Score", data.aggregate.score != null ? data.aggregate.score.toFixed(2) + " / 100" : "—"],
        ["Suggested Hike", data.aggregate.suggested_hike != null ? data.aggregate.suggested_hike + " %" : "—"],
        ["Approved Hike", data.aggregate.approved_hike != null ? data.aggregate.approved_hike + " %" : "—"],
        ["CTC at time", data.aggregate.ctc_at_time ? "₹" + Math.round(data.aggregate.ctc_at_time).toLocaleString("en-IN") + "/mo" : "—"],
        ["Reviewers", data.aggregate.submitted_reviews + " / " + data.aggregate.total_reviews + " submitted"],
      ];
      summaryRows.forEach((s, i) => {
        const row = wsSummary.addRow(s);
        styleBodyRow(row, { isAlt: i % 2 === 1 });
        row.getCell(1).font = { bold: true, size: 10, color: { argb: C.slate } };
      });

      // ════════════════════════════════════════════════════════════════
      //  SHEET 2 — Stakeholder Responses
      // ════════════════════════════════════════════════════════════════
      const ws1 = wb.addWorksheet("Stakeholder Responses", { views: [{ state: "frozen", ySplit: 1 }] });
      ws1.columns = [{ width: 22 }, { width: 36 }, { width: 12 }, { width: 24 }, { width: 70 }];

      data.stakeholder_responses.forEach((r, idx) => {
        writeBanner(ws1, `Reviewer ${idx + 1}: ${r.stakeholder_name} (${r.stakeholder_level || "—"})  ·  ${r.client_name}  ·  Status: ${r.status}`, 5);
        writeHeader(ws1, ["Section", "Criterion", "Score /4", "Rating", "Notes"]);

        // Group criteria by section so we only print the section name once
        // per group, instead of repeating "Performance" 8 times.
        const bySection = {};
        r.criteria_scores.forEach(cs => {
          if (!bySection[cs.section]) bySection[cs.section] = [];
          bySection[cs.section].push(cs);
        });

        let altCounter = 0;
        Object.entries(bySection).forEach(([section, items]) => {
          items.forEach((cs, i) => {
            const isFirstInGroup = i === 0;
            const row = ws1.addRow([
              isFirstInGroup ? section : "",      // section shown only on first row of its group
              cs.label,
              cs.score != null ? cs.score : "—",
              ratingLbl(cs.score),
              "",
            ]);
            styleBodyRow(row, { isAlt: altCounter % 2 === 1 });
            // Section column: bold + orange on the first row, transparent on the rest
            if (isFirstInGroup) {
              const cell = row.getCell(1);
              cell.font = { bold: true, size: 10, color: { argb: C.orange } };
            }
            altCounter++;
          });
        });

        // Text responses for this reviewer
        if (r.text && (r.text.feedback || r.text.prev_goals || r.text.next_goals)) {
          ws1.addRow([]);
          const txt = ws1.addRow(["", "Text Responses", "", "", "Answer"]);
          txt.height = 22;
          txt.eachCell({ includeEmpty: true }, cell => {
            cell.fill = { type: "pattern", pattern: "solid", fgColor: { argb: C.cream } };
            cell.font = { bold: true, color: { argb: C.creamFont }, size: 10 };
            cell.alignment = { vertical: "middle", indent: 1 };
            cell.border = thinBorder;
          });
          const addText = (label, value) => {
            if (value === null || value === undefined || value === "") return;
            const row = ws1.addRow(["", label, "", "", String(value)]);
            styleBodyRow(row);
            row.getCell(2).font = { bold: true, size: 10, color: { argb: C.slate } };
            row.getCell(5).alignment = { wrapText: true, vertical: "top" };
            row.height = Math.min(120, Math.max(28, Math.ceil(String(value).length / 90) * 16));
          };
          addText("Previous Goals", r.text.prev_goals);
          addText("Next Goals", r.text.next_goals);
          addText("Feedback", r.text.feedback);
          addText("Submitted at", r.text.submitted_at ? new Date(r.text.submitted_at).toLocaleString("en-IN") : null);
          addText("Submitted by", r.text.submitted_by);
        }

        // Divider rows between reviewers
        ws1.addRow([]);
        ws1.addRow([]);
      });

      // ════════════════════════════════════════════════════════════════
      //  SHEET 2 — Score Calculation
      // ════════════════════════════════════════════════════════════════
      const ws2 = wb.addWorksheet("Score Calculation", { views: [{ state: "frozen", ySplit: 2 }] });
      ws2.columns = [{ width: 28 }, { width: 12 }, { width: 14 }, { width: 14 }, { width: 18 }];
      writeBanner(ws2, "Per-Competency Weighted Scores", 5);
      writeHeader(ws2, ["Competency", "Weight %", "Avg Rating /4", "Score /100", "Weighted Score"]);
      perCompetency.forEach((c, i) => {
        const row = ws2.addRow([
          c.name,
          c.weight,
          c.avg != null ? Number(c.avg.toFixed(2)) : "—",
          c.normalized != null ? Number(c.normalized.toFixed(1)) : "—",
          c.weighted != null ? Number(c.weighted.toFixed(2)) : "—",
        ]);
        styleBodyRow(row, { isAlt: i % 2 === 1 });
        row.getCell(1).font = { bold: true, size: 10, color: { argb: C.slate } };
        row.getCell(2).font = { bold: true, size: 10, color: { argb: C.orange } };
      });
      // Final-score footer row (dark navy, orange value)
      const fsRow = ws2.addRow(["Final Score", "", "", "", data.aggregate.score != null ? Number(data.aggregate.score.toFixed(2)) : "—"]);
      fsRow.height = 24;
      fsRow.eachCell({ includeEmpty: true }, cell => {
        cell.fill = { type: "pattern", pattern: "solid", fgColor: { argb: C.darkNavy } };
        cell.font = { bold: true, color: { argb: cell.col === 5 ? C.orange : C.white }, size: 12 };
        cell.alignment = { vertical: "middle", horizontal: cell.col === 5 ? "right" : "left", indent: 1 };
        cell.border = thinBorder;
      });

      ws2.addRow([]);
      writeBanner(ws2, "Stakeholder Weight Distribution", 5);
      writeHeader(ws2, ["Stakeholder", "Level", "Weight %", "", ""]);
      stakeholderSlices.forEach((s, i) => {
        const row = ws2.addRow([s.name, s.level || "", s.weightPct, "", ""]);
        styleBodyRow(row, { isAlt: i % 2 === 1 });
      });

      // ════════════════════════════════════════════════════════════════
      //  SHEET 3 — Hike Breakdown
      // ════════════════════════════════════════════════════════════════
      const ws3 = wb.addWorksheet("Hike Breakdown");
      ws3.columns = [{ width: 8 }, { width: 36 }, { width: 22 }];
      const ctc = data.aggregate.ctc_at_time || 0;
      const hikePct = data.aggregate.approved_hike != null ? data.aggregate.approved_hike : data.aggregate.suggested_hike;
      const hikeAmt = hikePct != null && ctc > 0 ? ctc * hikePct / 100 : null;

      writeBanner(ws3, "Hike Computation Steps", 3);
      writeHeader(ws3, ["Step", "Label", "Value"]);
      const hikeSteps = [
        ["1", "Final Score", data.aggregate.score != null ? data.aggregate.score.toFixed(2) + " / 100" : "—"],
        ["2", "Suggested Hike (from tier)", data.aggregate.suggested_hike != null ? data.aggregate.suggested_hike + " %" : "—"],
        ["3", "Approved Hike (admin entered)", data.aggregate.approved_hike != null ? data.aggregate.approved_hike + " %" : "—"],
      ];
      hikeSteps.forEach((s, i) => {
        const row = ws3.addRow(s);
        styleBodyRow(row, { isAlt: i % 2 === 1 });
        row.getCell(1).font = { bold: true, size: 10, color: { argb: C.orange } };
        row.getCell(1).alignment = { horizontal: "center" };
      });

      ws3.addRow([]);
      writeBanner(ws3, "CTC Impact Projection", 3);
      writeHeader(ws3, ["", "Item", "Amount (₹/mo)"]);
      [
        ["", "Current CTC", ctc],
        ["", hikePct != null ? "Hike Amount (" + hikePct + "%)" : "Hike Amount", hikeAmt != null ? Math.round(hikeAmt) : "—"],
        ["", "New CTC", hikeAmt != null ? Math.round(ctc + hikeAmt) : "—"],
      ].forEach((s, i) => {
        const row = ws3.addRow(s);
        styleBodyRow(row, { isAlt: i % 2 === 1 });
        if (i === 2) {
          // Highlight final "New CTC" row
          row.eachCell({ includeEmpty: true }, cell => {
            cell.fill = { type: "pattern", pattern: "solid", fgColor: { argb: C.greenBg } };
            cell.font = { bold: true, size: 11, color: { argb: "FF065F46" } };
          });
        }
      });

      const buffer = await wb.xlsx.writeBuffer();
      triggerDownload(new Blob([buffer]), `scoring_breakdown_${data.employee.code}_${data.cycle.quarter_label.replace(/\s+/g, "_")}.xlsx`);
    } catch (e) {
      console.error("[BreakdownDrawer.exportExcel]", e);
    } finally {
      setExporting(false);
    }
  };

  const exportPdf = async () => {
    if (!data) return;
    setExporting(true);
    try {
      const jsPDFCtor = await loadJsPDF();
      const doc = new jsPDFCtor({ unit: "pt", format: "a4" });
      const watermark = await loadWatermarkDataUrl().catch(() => null);
      if (watermark) applyPdfWatermark(doc, watermark);
      doc.setFontSize(16);
      doc.text("Scoring Breakdown", 40, 50);
      doc.setFontSize(10);
      doc.text(`${data.employee.name} (${data.employee.code}) · ${data.cycle.quarter_label}`, 40, 68);

      // Summary block
      doc.autoTable({
        startY: 84,
        head: [["Metric", "Value"]],
        body: [
          ["Final Score (/100)", data.aggregate.score != null ? data.aggregate.score.toFixed(2) : "—"],
          ["Suggested Hike %", data.aggregate.suggested_hike != null ? data.aggregate.suggested_hike + "%" : "—"],
          ["Approved Hike %", data.aggregate.approved_hike != null ? data.aggregate.approved_hike + "%" : "—"],
          ["CTC at time", data.aggregate.ctc_at_time || 0],
          ["Reviewers", data.aggregate.submitted_reviews + " / " + data.aggregate.total_reviews + " submitted"],
        ],
        styles: { fontSize: 9 },
        headStyles: { fillColor: [13, 27, 42], textColor: 255 },
      });

      // Per-competency weighted scores (matches the in-app table)
      doc.autoTable({
        startY: (doc.lastAutoTable.finalY || 84) + 16,
        head: [["Competency", "Weight %", "Avg /4", "Score /100", "Weighted"]],
        body: perCompetency.map(c => [
          c.name, c.weight,
          c.avg != null ? c.avg.toFixed(2) : "—",
          c.normalized != null ? c.normalized.toFixed(1) : "—",
          c.weighted != null ? c.weighted.toFixed(2) : "—",
        ]),
        styles: { fontSize: 8 },
        headStyles: { fillColor: [232, 82, 10], textColor: 255 },
        didDrawPage: () => watermark && applyPdfWatermark(doc, watermark),
      });

      // Per-stakeholder responses
      data.stakeholder_responses.forEach(r => {
        doc.addPage();
        if (watermark) applyPdfWatermark(doc, watermark);
        doc.setFontSize(13);
        doc.text(r.stakeholder_name + (r.stakeholder_level === "client" ? " (Client Level)" : " (Dept Level)"), 40, 50);
        doc.setFontSize(9);
        doc.text(`${r.stakeholder_designation || ""} · ${r.client_name} · ${r.status}`, 40, 66);
        doc.autoTable({
          startY: 82,
          head: [["Criterion", "Section", "Score /4"]],
          body: r.criteria_scores.map(cs => [cs.label, cs.section, cs.score != null ? cs.score : "—"]),
          styles: { fontSize: 8 },
          headStyles: { fillColor: [13, 27, 42], textColor: 255 },
        });
        if (r.text && (r.text.feedback || r.text.prev_goals || r.text.next_goals)) {
          let y = (doc.lastAutoTable.finalY || 82) + 16;
          doc.setFontSize(10);
          doc.text("Feedback:", 40, y); y += 12;
          doc.setFontSize(9);
          ["feedback", "prev_goals", "next_goals"].forEach(k => {
            if (r.text[k]) {
              const lines = doc.splitTextToSize(`${k.replace("_", " ")}: ${r.text[k]}`, 515);
              doc.text(lines, 40, y); y += lines.length * 12 + 4;
            }
          });
        }
      });

      doc.save(`scoring_breakdown_${data.employee.code}_${data.cycle.quarter_label.replace(/\s+/g, "_")}.pdf`);
    } catch (e) {
      console.error("[BreakdownDrawer.exportPdf]", e);
    } finally {
      setExporting(false);
    }
  };

  return (
    <div className="fade-in" style={{
      position: "fixed", inset: 0, background: "rgba(13,27,42,0.55)", backdropFilter: "blur(3px)",
      zIndex: 300, display: "flex", justifyContent: "flex-end", animation: "fadeIn .2s ease",
    }} onClick={e => { if (e.target === e.currentTarget) onClose(); }}>
      <div style={{
        width: "min(860px, 96vw)", height: "100vh", background: "#fff",
        boxShadow: "-8px 0 32px rgba(0,0,0,0.18)",
        display: "flex", flexDirection: "column",
      }}>
        {/* Header */}
        <div style={{ background: "linear-gradient(135deg,#0D1B2A,#1E3A5F)", padding: "20px 26px", flexShrink: 0 }}>
          <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", marginBottom: data ? 16 : 0 }}>
            <div style={{ display: "flex", alignItems: "center", gap: 12 }}>
              <Avatar name={emp.name} size={40} />
              <div>
                <div style={{ fontFamily: "'Sora',sans-serif", fontWeight: 800, fontSize: 17, color: "#fff" }}>
                  {emp.name} <span style={{ fontSize: 11, fontFamily: "'JetBrains Mono',monospace", color: "rgba(255,255,255,.55)", marginLeft: 6 }}>{emp.code}</span>
                </div>
                <div style={{ fontSize: 11, color: "rgba(255,255,255,.55)", marginTop: 2 }}>
                  Scoring breakdown {data && data.cycle ? "· " + data.cycle.quarter_label : ""}
                </div>
              </div>
            </div>
            <button onClick={onClose} style={{
              background: "rgba(255,255,255,.15)", border: "none", borderRadius: 9, color: "#fff",
              width: 34, height: 34, fontSize: 18, cursor: "pointer", display: "flex", alignItems: "center", justifyContent: "center"
            }}>×</button>
          </div>

          {/* Summary stat strip */}
          {data && (
            <div style={{ display: "grid", gridTemplateColumns: "repeat(4,1fr)", gap: 8 }}>
              {[
                { label: "Final Score", value: data.aggregate.score != null ? data.aggregate.score.toFixed(2) + "/100" : "—", color: "#60A5FA", bg: "rgba(96,165,250,0.15)" },
                { label: "Suggested %", value: data.aggregate.suggested_hike != null ? data.aggregate.suggested_hike + "%" : "—", color: "#FBBF24", bg: "rgba(251,191,36,0.15)" },
                { label: "Approved %", value: data.aggregate.approved_hike != null ? data.aggregate.approved_hike + "%" : "—", color: "#34D399", bg: "rgba(52,211,153,0.15)" },
                { label: "Reviewers", value: data.aggregate.submitted_reviews + " / " + data.aggregate.total_reviews, color: "#C084FC", bg: "rgba(192,132,252,0.15)" },
              ].map((s, i) => (
                <div key={i} style={{ background: s.bg, borderRadius: 9, padding: "9px 12px", border: "1px solid " + s.color + "33" }}>
                  <div style={{ fontSize: 9, color: "rgba(255,255,255,.55)", fontWeight: 700, letterSpacing: 0.5, marginBottom: 3, textTransform: "uppercase" }}>{s.label}</div>
                  <div style={{ fontFamily: "'JetBrains Mono',monospace", fontWeight: 700, fontSize: 14, color: s.color }}>{s.value}</div>
                </div>
              ))}
            </div>
          )}
        </div>

        {/* Tabs */}
        {data && !loading && !error && (
          <div style={{ display: "flex", borderBottom: "1px solid #E2E8F0", background: "#FAFBFF", flexShrink: 0 }}>
            {TABS.map((t, i) => (
              <button key={i} onClick={() => setTab(i)} style={{
                flex: 1, padding: "11px 8px", border: "none", cursor: "pointer", fontSize: 11,
                fontWeight: tab === i ? 700 : 500,
                background: tab === i ? "#FFF5F0" : "transparent",
                color: tab === i ? "#E8520A" : "#64748B",
                borderBottom: tab === i ? "2.5px solid #E8520A" : "2.5px solid transparent",
                whiteSpace: "nowrap", transition: "all .15s",
              }}>
                {t}
              </button>
            ))}
          </div>
        )}

        {/* Body */}
        <div style={{ flex: 1, overflowY: "auto", padding: "22px 26px" }}>
          {loading && (
            <div style={{ textAlign: "center", padding: "60px 0", color: "#94A3B8", fontSize: 13 }}>Loading breakdown…</div>
          )}
          {error && !loading && (
            <div style={{ background: "#FEF2F2", border: "1px solid #FECACA", borderRadius: 10, padding: "12px 16px", color: "#991B1B", fontSize: 13 }}>
              {error}
            </div>
          )}

          {/* ── TAB 0: Stakeholder Responses ────────────────────────── */}
          {data && !loading && !error && tab === 0 && (
            <>
              <div style={{ fontSize: 12, color: "#64748B", marginBottom: 14, lineHeight: 1.6 }}>
                {data.stakeholder_responses.length} stakeholder{data.stakeholder_responses.length !== 1 ? "s" : ""} associated with <strong style={{ color: "#0D1B2A" }}>{data.employee.name}</strong> this quarter.
              </div>
              {data.stakeholder_responses.length === 0 && (
                <div style={{ background: "#F8FAFC", border: "1px solid #E2E8F0", borderRadius: 10, padding: "16px 20px", color: "#64748B", fontSize: 13 }}>
                  No stakeholder reviews exist for this employee in this cycle yet.
                </div>
              )}
              {data.stakeholder_responses.map(r => {
                const bySection = {};
                for (const cs of r.criteria_scores) {
                  if (!bySection[cs.section]) bySection[cs.section] = [];
                  bySection[cs.section].push(cs);
                }
                const STATUS_COLORS = {
                  "Submitted": { bg: "#F0FDF4", c: "#065F46", bd: "#A7F3D0" },
                  "Approved": { bg: "#EFF6FF", c: "#1D4ED8", bd: "#BFDBFE" },
                  "In Progress": { bg: "#FFFBEB", c: "#92400E", bd: "#FDE68A" },
                  "Initiated": { bg: "#F5F3FF", c: "#6D28D9", bd: "#DDD6FE" },
                  "Not Started": { bg: "#F8FAFC", c: "#94A3B8", bd: "#E2E8F0" },
                };
                const sc = STATUS_COLORS[r.status] || STATUS_COLORS["Not Started"];
                return (
                  <div key={r.review_id} style={{ border: "1px solid #E2E8F0", borderRadius: 12, overflow: "hidden", marginBottom: 14 }}>
                    <div style={{ padding: "12px 16px", background: "#F8FAFC", borderBottom: "1px solid #E2E8F0", display: "flex", alignItems: "center", gap: 10, flexWrap: "wrap" }}>
                      <Avatar name={r.stakeholder_name} size={30} />
                      <div style={{ flex: 1, minWidth: 0 }}>
                        <div style={{ fontWeight: 700, fontSize: 13, color: "#0D1B2A" }}>{r.stakeholder_name}</div>
                        <div style={{ fontSize: 11, color: "#64748B" }}>{r.stakeholder_designation || "—"} · {r.client_name}</div>
                      </div>
                      <span style={{ fontSize: 10, fontWeight: 700, background: r.stakeholder_level === "client" ? "#EFF6FF" : "#F5F3FF", color: r.stakeholder_level === "client" ? "#1D4ED8" : "#6D28D9", border: "1px solid " + (r.stakeholder_level === "client" ? "#BFDBFE" : "#DDD6FE"), borderRadius: 100, padding: "2px 8px", textTransform: "uppercase", letterSpacing: 0.3 }}>
                        {r.stakeholder_level === "client" ? "Client Level" : "Dept Level"}
                      </span>
                      <span style={{ fontSize: 10, fontWeight: 700, background: sc.bg, color: sc.c, border: "1px solid " + sc.bd, borderRadius: 100, padding: "2px 8px" }}>
                        {r.status}
                      </span>
                      {r.normalized_score != null && (
                        <span style={{ fontFamily: "'JetBrains Mono',monospace", fontWeight: 800, fontSize: 12, color: scoreColor(r.normalized_score), background: "#fff", border: "1px solid " + scoreColor(r.normalized_score) + "55", borderRadius: 8, padding: "3px 9px" }}>
                          {r.normalized_score.toFixed(1)}/100
                        </span>
                      )}
                    </div>
                    <div style={{ padding: "12px 16px" }}>
                      {Object.keys(bySection).length === 0 && (
                        <div style={{ fontSize: 12, color: "#94A3B8", fontStyle: "italic" }}>No scores recorded yet.</div>
                      )}
                      {Object.entries(bySection).map(([section, items]) => (
                        <div key={section} style={{ marginBottom: 14 }}>
                          {/* Section label — slightly darker pill so the form's
                              own grouping (Performance / Interpersonal / Values
                              / Conduct) is visually obvious without being heavy. */}
                          <div style={{
                            display: "inline-block",
                            fontSize: 11, fontWeight: 700, color: "#1E293B",
                            background: "#E2E8F0", border: "1px solid #CBD5E1",
                            borderRadius: 6, padding: "3px 10px", marginBottom: 8,
                            letterSpacing: 0.3,
                          }}>{section}</div>
                          <div style={{ display: "grid", gridTemplateColumns: "repeat(2,1fr)", gap: 6 }}>
                            {items.map(cs => (
                              <div key={cs.criterion_id} style={{ background: "#FAFBFF", border: "1px solid #F1F5F9", borderRadius: 7, padding: "7px 10px", display: "flex", alignItems: "center", gap: 8 }}>
                                <div style={{ flex: 1, fontSize: 11, color: "#475569" }}>{cs.label}</div>
                                {cs.score != null ? (
                                  <span title={ratingLabel(cs.score)} style={{ fontFamily: "'JetBrains Mono',monospace", fontWeight: 700, fontSize: 11, color: ratingColor(cs.score), background: "#fff", border: "1px solid " + ratingColor(cs.score) + "44", borderRadius: 6, padding: "2px 7px", whiteSpace: "nowrap" }}>
                                    {cs.score}/4
                                  </span>
                                ) : <span style={{ fontSize: 10, color: "#CBD5E1" }}>—</span>}
                              </div>
                            ))}
                          </div>
                        </div>
                      ))}
                      {r.text && (r.text.feedback || r.text.prev_goals || r.text.next_goals) && (
                        <div style={{ marginTop: 10, background: "#FFFBEB", border: "1px solid #FDE68A", borderRadius: 8, padding: "10px 12px" }}>
                          {r.text.feedback && (
                            <div style={{ marginBottom: r.text.prev_goals || r.text.next_goals ? 8 : 0 }}>
                              <div style={{ fontSize: 9, fontWeight: 700, color: "#92400E", textTransform: "uppercase", letterSpacing: 0.4, marginBottom: 3 }}>💬 Feedback</div>
                              <div style={{ fontSize: 12, color: "#78350F", lineHeight: 1.6, whiteSpace: "pre-wrap" }}>{r.text.feedback}</div>
                            </div>
                          )}
                          {r.text.prev_goals && (
                            <div style={{ marginBottom: r.text.next_goals ? 8 : 0 }}>
                              <div style={{ fontSize: 9, fontWeight: 700, color: "#92400E", textTransform: "uppercase", letterSpacing: 0.4, marginBottom: 3 }}>Previous Goals</div>
                              <div style={{ fontSize: 12, color: "#78350F", lineHeight: 1.6, whiteSpace: "pre-wrap" }}>{r.text.prev_goals}</div>
                            </div>
                          )}
                          {r.text.next_goals && (
                            <div>
                              <div style={{ fontSize: 9, fontWeight: 700, color: "#92400E", textTransform: "uppercase", letterSpacing: 0.4, marginBottom: 3 }}>Next Goals</div>
                              <div style={{ fontSize: 12, color: "#78350F", lineHeight: 1.6, whiteSpace: "pre-wrap" }}>{r.text.next_goals}</div>
                            </div>
                          )}
                        </div>
                      )}
                      <div style={{ marginTop: 10, fontSize: 10, color: "#94A3B8" }}>
                        Submitted: {fmtDateTime(r.text && r.text.submitted_at) || fmtDateTime(r.submitted_at)}
                      </div>
                    </div>
                  </div>
                );
              })}
            </>
          )}

          {/* ── TAB 1: Score Calculation ────────────────────────── */}
          {data && !loading && !error && tab === 1 && (
            <>
              <div style={{ background: "#EFF6FF", border: "1px solid #BFDBFE", borderRadius: 10, padding: "11px 14px", marginBottom: 14, fontSize: 12, color: "#1E40AF", lineHeight: 1.7 }}>
                <strong>How score is calculated:</strong> Each competency's weighted score = (Avg stakeholder rating / 4) × 100 × competency weight%. Final score = sum of all weighted competency scores.
              </div>
              <PerCompetencyTable perCompetency={perCompetency} aggregate={data.aggregate} weightedSum={competencyWeightedSum} ratingColor={ratingColor} scoreColor={scoreColor} />
              <StakeholderWeightDistribution slices={stakeholderSlices} />
            </>
          )}

          {/* ── TAB 2: Hike Breakdown ───────────────────────────── */}
          {data && !loading && !error && tab === 2 && (
            <HikeBreakdown data={data} MULT={MULT} />
          )}

          {/* ── TAB 3: Radar View ───────────────────────────────── */}
          {data && !loading && !error && tab === 3 && (
            <RadarTab perCompetency={perCompetency} scoreColor={scoreColor} />
          )}
        </div>

        {/* Footer with Export buttons */}
        <div style={{ padding: "12px 26px", borderTop: "1px solid #E2E8F0", background: "#F8FAFC", flexShrink: 0, display: "flex", justifyContent: "space-between", alignItems: "center", gap: 10, flexWrap: "wrap" }}>
          <div style={{ fontSize: 11, color: "#94A3B8" }}>
            🔒 Internal view — never shared with employees or stakeholders
          </div>
          <div style={{ display: "flex", gap: 8, alignItems: "center" }}>
            <button onClick={exportExcel} disabled={!data || exporting}
              style={{
                padding: "8px 14px", borderRadius: 8,
                border: "1.5px solid " + (!data || exporting ? "#E2E8F0" : "#A7F3D0"),
                background: !data || exporting ? "#F8FAFC" : "#F0FDF4",
                color: !data || exporting ? "#94A3B8" : "#065F46",
                fontSize: 12, fontWeight: 700,
                cursor: !data || exporting ? "not-allowed" : "pointer",
                display: "inline-flex", alignItems: "center", gap: 5,
              }}>
              📊 Export XLS
            </button>
            <button onClick={exportPdf} disabled={!data || exporting}
              style={{
                padding: "8px 14px", borderRadius: 8,
                border: "1.5px solid " + (!data || exporting ? "#E2E8F0" : "#FED7AA"),
                background: !data || exporting ? "#F8FAFC" : "#FFF5F0",
                color: !data || exporting ? "#94A3B8" : "#9A3412",
                fontSize: 12, fontWeight: 700,
                cursor: !data || exporting ? "not-allowed" : "pointer",
                display: "inline-flex", alignItems: "center", gap: 5,
              }}>
              📄 Export PDF
            </button>
            <button onClick={onClose} style={{
              padding: "8px 16px", borderRadius: 8, border: "none", background: "#0D1B2A",
              color: "#fff", fontSize: 12, fontWeight: 600, cursor: "pointer",
            }}>Close</button>
          </div>
        </div>
      </div>
    </div>
  );
}

// Per-competency aggregate table (used in Score Calculation tab).
// Each row collapses all the section's criteria into one row so the table
// stays readable even when there are 17+ criteria — the design mockup uses
// this exact format.
function PerCompetencyTable({ perCompetency, aggregate, weightedSum, ratingColor, scoreColor }) {
  // Track the visual weight bar's max so the per-row fill is comparable
  const maxWeighted = Math.max(1, ...perCompetency.map(c => c.weighted || 0));
  return (
    <div style={{ border: "1px solid #E2E8F0", borderRadius: 12, overflow: "hidden", marginBottom: 18 }}>
      <div style={{ background: "#F8FAFC", padding: "10px 16px", borderBottom: "1px solid #E2E8F0" }}>
        <div style={{ fontFamily: "'Sora',sans-serif", fontWeight: 700, fontSize: 13, color: "#0D1B2A" }}>
          Per-Competency Weighted Scores
        </div>
      </div>
      <table style={{ width: "100%", borderCollapse: "collapse", fontSize: 12 }}>
        <thead>
          <tr style={{ background: "#FAFBFF" }}>
            {["Competency", "Weight", "Avg Rating /4", "Score /100", "Weighted Score"].map(h => (
              <th key={h} style={{ padding: "10px 14px", textAlign: "left", fontSize: 10, fontWeight: 700, color: "#64748B", letterSpacing: 0.5, textTransform: "uppercase", borderBottom: "1px solid #E2E8F0" }}>{h}</th>
            ))}
          </tr>
        </thead>
        <tbody>
          {perCompetency.map(c => (
            <tr key={c.id || c.name} style={{ borderBottom: "1px solid #F1F5F9" }}>
              <td style={{ padding: "11px 14px", fontWeight: 600, color: "#0D1B2A" }}>{c.name}</td>
              <td style={{ padding: "11px 14px" }}>
                <span style={{ fontFamily: "'JetBrains Mono',monospace", fontSize: 12, fontWeight: 600, color: "#E8520A" }}>{c.weight}%</span>
              </td>
              <td style={{ padding: "11px 14px" }}>
                {c.avg != null ? (
                  <div style={{ display: "flex", alignItems: "center", gap: 6 }}>
                    <div style={{ display: "flex", gap: 2 }}>
                      {[1, 2, 3, 4].map(n => (
                        <div key={n} style={{
                          width: 12, height: 12, borderRadius: 3,
                          background: n <= Math.round(c.avg) ? ratingColor(Math.round(c.avg)) : "#E2E8F0",
                        }} />
                      ))}
                    </div>
                    <span style={{ fontFamily: "'JetBrains Mono',monospace", fontSize: 11, color: "#475569" }}>{c.avg.toFixed(2)}</span>
                  </div>
                ) : <span style={{ color: "#CBD5E1" }}>—</span>}
              </td>
              <td style={{ padding: "11px 14px" }}>
                {c.normalized != null ? (
                  <span style={{ fontFamily: "'JetBrains Mono',monospace", fontWeight: 700, color: scoreColor(c.normalized) }}>{c.normalized.toFixed(1)}</span>
                ) : <span style={{ color: "#CBD5E1" }}>—</span>}
              </td>
              <td style={{ padding: "11px 14px" }}>
                {c.weighted != null ? (
                  <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
                    <div style={{ flex: 1, height: 6, background: "#F1F5F9", borderRadius: 100, overflow: "hidden", minWidth: 60 }}>
                      <div style={{ height: "100%", width: `${Math.min(100, (c.weighted / maxWeighted) * 100)}%`, background: "linear-gradient(90deg,#E8520A,#FF8C5A)", borderRadius: 100 }} />
                    </div>
                    <span style={{ fontFamily: "'JetBrains Mono',monospace", fontWeight: 700, fontSize: 12, color: "#0D1B2A", minWidth: 44, textAlign: "right" }}>{c.weighted.toFixed(2)}</span>
                  </div>
                ) : <span style={{ color: "#CBD5E1" }}>—</span>}
              </td>
            </tr>
          ))}
        </tbody>
        <tfoot>
          <tr style={{ background: "#0D1B2A" }}>
            <td colSpan={4} style={{ padding: "12px 14px", fontFamily: "'Sora',sans-serif", fontWeight: 700, fontSize: 13, color: "#fff" }}>
              Final Score
              {weightedSum > 0 && aggregate.score != null && Math.abs(weightedSum - aggregate.score) > 1.5 && (
                <span style={{ display: "block", fontSize: 10, fontWeight: 400, color: "rgba(255,255,255,.45)", marginTop: 2 }}>
                  Weighted sum {weightedSum.toFixed(2)} — backend score below uses unweighted criterion average.
                </span>
              )}
            </td>
            <td style={{ padding: "12px 14px" }}>
              <span style={{ fontFamily: "'JetBrains Mono',monospace", fontWeight: 800, fontSize: 18, color: "#60A5FA" }}>
                {aggregate.score != null ? aggregate.score.toFixed(2) : "—"}
              </span>
              <span style={{ fontSize: 11, color: "rgba(255,255,255,.4)", marginLeft: 4 }}>/100</span>
            </td>
          </tr>
        </tfoot>
      </table>
    </div>
  );
}

// Visual breakdown of how each stakeholder's review weighted into the final
// score. In this app every submitted reviewer contributes equally (the
// backend averages all criterion scores across reviews), so each slice is
// 1/N — the bar makes that explicit instead of looking like one reviewer
// owns the whole score.
function StakeholderWeightDistribution({ slices }) {
  if (!slices || slices.length === 0) {
    return (
      <div style={{ background: "#F8FAFC", borderRadius: 10, padding: "14px 16px", border: "1px solid #E2E8F0", fontSize: 12, color: "#94A3B8" }}>
        No submitted stakeholder reviews to weight.
      </div>
    );
  }
  const palette = ["#E8520A", "#3B82F6", "#10B981", "#8B5CF6", "#F59E0B", "#06B6D4", "#EC4899"];
  return (
    <div style={{ background: "#F8FAFC", borderRadius: 10, padding: "14px 16px", border: "1px solid #E2E8F0" }}>
      <div style={{ fontSize: 12, fontWeight: 700, color: "#475569", marginBottom: 10, textTransform: "uppercase", letterSpacing: 0.5 }}>
        Stakeholder Weight Distribution
      </div>
      <div style={{ display: "flex", gap: 3, height: 18, borderRadius: 100, overflow: "hidden", marginBottom: 10 }}>
        {slices.map((s, i) => (
          <div key={s.id || i} title={s.name + " · " + s.weightPct + "%"} style={{ flex: s.weightPct, background: palette[i % palette.length] }} />
        ))}
      </div>
      <div style={{ display: "flex", gap: 12, flexWrap: "wrap" }}>
        {slices.map((s, i) => (
          <div key={s.id || i} style={{ display: "flex", alignItems: "center", gap: 6 }}>
            <div style={{ width: 10, height: 10, borderRadius: 3, background: palette[i % palette.length] }} />
            <span style={{ fontSize: 11, color: "#475569" }}>
              {s.name} {s.level ? <span style={{ color: "#94A3B8" }}>({s.level})</span> : null}: <strong>{s.weightPct}%</strong>
            </span>
          </div>
        ))}
      </div>
    </div>
  );
}

// Radar chart of per-competency normalized scores (used in Radar View tab).
// We plot the 6 competencies (sections) — not the 17+ individual criteria —
// because the chart becomes unreadable past ~8 axes.
function RadarTab({ perCompetency, scoreColor }) {
  const data = (perCompetency || []).filter(c => c.normalized != null);
  if (data.length === 0) {
    return (
      <div style={{ background: "#F8FAFC", border: "1px solid #E2E8F0", borderRadius: 10, padding: "30px", textAlign: "center", color: "#94A3B8", fontSize: 13 }}>
        No score data to plot.
      </div>
    );
  }
  const size = 360, cx = size / 2, cy = size / 2, R = 130;
  const angle = i => (Math.PI * 2 * i / data.length) - Math.PI / 2;
  const ringValues = [25, 50, 75, 100];
  const pointFor = (val, i) => {
    const r = (val / 100) * R;
    return [cx + r * Math.cos(angle(i)), cy + r * Math.sin(angle(i))];
  };
  const polyPoints = data.map((c, i) => pointFor(c.normalized, i).join(",")).join(" ");
  return (
    <div>
      <div style={{ fontFamily: "'Sora',sans-serif", fontWeight: 700, fontSize: 13, color: "#0D1B2A", marginBottom: 4 }}>
        Per-competency score radar
      </div>
      <div style={{ fontSize: 12, color: "#64748B", marginBottom: 14 }}>
        Aggregated 0–100 score per competency across all submitted stakeholders. Each axis is one competency from the weights panel.
      </div>

      <div style={{ display: "flex", justifyContent: "center", marginBottom: 18 }}>
        <svg width={size} height={size} style={{ overflow: "visible" }}>
          {ringValues.map(v => (
            <polygon key={v}
              points={data.map((_, i) => pointFor(v, i).join(",")).join(" ")}
              fill="none" stroke="#E2E8F0" strokeWidth={1} />
          ))}
          {data.map((_, i) => {
            const [x, y] = pointFor(100, i);
            return <line key={i} x1={cx} y1={cy} x2={x} y2={y} stroke="#E2E8F0" strokeWidth={1} />;
          })}
          <polygon points={polyPoints} fill="#E8520A" fillOpacity={0.18} stroke="#E8520A" strokeWidth={2} />
          {data.map((c, i) => {
            const [x, y] = pointFor(c.normalized, i);
            return <circle key={c.id || c.name} cx={x} cy={y} r={3.5} fill="#E8520A" />;
          })}
          {data.map((c, i) => {
            const [x, y] = pointFor(118, i);
            const anchor = Math.abs(x - cx) < 5 ? "middle" : x > cx ? "start" : "end";
            return (
              <text key={(c.id || c.name) + "-label"} x={x} y={y} textAnchor={anchor} dominantBaseline="middle"
                style={{ fontSize: 11, fontFamily: "'DM Sans',sans-serif", fontWeight: 600, fill: "#475569" }}>
                {c.name}
              </text>
            );
          })}
        </svg>
      </div>

      <div style={{ display: "grid", gridTemplateColumns: "repeat(3,1fr)", gap: 8 }}>
        {perCompetency.map(c => (
          <div key={c.id || c.name} style={{ background: "#F8FAFC", borderRadius: 8, padding: "9px 11px", border: "1px solid #E2E8F0" }}>
            <div style={{ fontSize: 9, color: "#94A3B8", fontWeight: 700, textTransform: "uppercase", letterSpacing: 0.4, marginBottom: 4 }}>{c.name}</div>
            <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-end" }}>
              <div style={{ fontFamily: "'JetBrains Mono',monospace", fontWeight: 800, fontSize: 16, color: c.normalized != null ? scoreColor(c.normalized) : "#94A3B8" }}>
                {c.normalized != null ? c.normalized.toFixed(1) : "—"}
              </div>
              <div style={{ fontSize: 11, color: "#94A3B8" }}>wt: {c.weight}%</div>
            </div>
            <div style={{ height: 4, background: "#F1F5F9", borderRadius: 100, overflow: "hidden", marginTop: 6 }}>
              <div style={{ height: "100%", width: `${c.normalized || 0}%`, background: c.normalized != null ? scoreColor(c.normalized) : "#E2E8F0", borderRadius: 100 }} />
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}


function HikeBreakdown({ data, MULT }) {
  const score = data.aggregate.score;
  const sug = data.aggregate.suggested_hike;
  const approved = data.aggregate.approved_hike;
  const ctc = data.aggregate.ctc_at_time || 0;
  const hikePct = approved != null ? approved : sug;
  const hikeAmt = hikePct != null && ctc > 0 ? (ctc * hikePct / 100) : null;
  const newCtc = hikeAmt != null ? ctc + hikeAmt : null;

  // Tier the backend uses to pick the suggested hike % (matches listScoring's CASE).
  const tierFor = s => s == null ? null
    : s >= 90 ? { label: "Excellent (≥90)", pct: 22 }
      : s >= 80 ? { label: "Strong (≥80)", pct: 18 }
        : s >= 70 ? { label: "Good (≥70)", pct: 14 }
          : s >= 60 ? { label: "Fair (≥60)", pct: 10 }
            : { label: "Below (<60)", pct: 6 };
  const tier = tierFor(score);

  // Render one step row + the connecting line below it (mockup style).
  const Step = ({ icon, n, label, value, unit, color, last, highlight }) => (
    <>
      <div style={{
        display: "flex", gap: 14, alignItems: "center",
        padding: "14px 16px",
        background: highlight ? "#F0FDF4" : "#F8FAFC",
        border: "1px solid " + (highlight ? "#BBF7D0" : "#E2E8F0"),
        borderRadius: last ? "0 0 10px 10px" : (n === 1 ? "10px 10px 0 0" : 0),
      }}>
        <div style={{
          width: 34, height: 34, borderRadius: 9,
          background: color + "22", border: "1.5px solid " + color + "44",
          display: "flex", alignItems: "center", justifyContent: "center",
          fontSize: 15, flexShrink: 0, color: color, fontWeight: 700,
        }}>{icon}</div>
        <div style={{ flex: 1 }}>
          <div style={{ fontSize: 11, color: "#64748B", fontWeight: 600, marginBottom: 3 }}>Step {n}</div>
          <div style={{ fontSize: 13, fontWeight: 600, color: "#0D1B2A" }}>{label}</div>
        </div>
        <div style={{ fontFamily: "'JetBrains Mono',monospace", fontWeight: 800, fontSize: 18, color, whiteSpace: "nowrap" }}>
          {value}<span style={{ fontSize: 12, fontWeight: 400, color: "#94A3B8" }}>{unit}</span>
        </div>
      </div>
      {!last && (
        <div style={{ display: "flex", justifyContent: "center", height: 20, alignItems: "center" }}>
          <div style={{ width: 1, height: "100%", background: "#E2E8F0" }} />
        </div>
      )}
    </>
  );

  const steps = [
    { icon: "📊", label: "Final Score", value: score != null ? score.toFixed(2) : "—", unit: "/100", color: "#3B82F6" },
    { icon: "🎯", label: tier ? `Performance Tier — ${tier.label}` : "Performance Tier", value: tier ? "" : "—", unit: tier ? tier.label.split(" ")[0] : "", color: "#8B5CF6" },
    { icon: "💰", label: "Suggested Hike (from tier)", value: sug != null ? sug.toFixed(2) : "—", unit: "%", color: "#E8520A" },
    { icon: "✓", label: "Approved Hike (admin entered)", value: approved != null ? approved.toFixed(2) : "—", unit: "%", color: "#10B981", highlight: true },
  ];

  return (
    <>
      <div style={{ background: "#F0FDF4", border: "1px solid #BBF7D0", borderRadius: 10, padding: "11px 14px", marginBottom: 14, fontSize: 12, color: "#166534", lineHeight: 1.7 }}>
        <strong>Hike formula:</strong> the suggested hike % is derived from the final score tier — Score ≥90 → 22%, ≥80 → 18%, ≥70 → 14%, ≥60 → 10%, else 6%.
        {tier && score != null && (
          <div style={{ fontSize: 11, color: "#15803D", marginTop: 4 }}>
            Score <strong>{score.toFixed(2)}</strong> falls in tier <strong>{tier.label}</strong> → suggested hike <strong>{tier.pct}%</strong>.
          </div>
        )}
      </div>

      <div style={{ display: "flex", flexDirection: "column", marginBottom: 18 }}>
        {steps.map((s, i, arr) => (
          <Step key={i} n={i + 1} {...s} last={i === arr.length - 1} />
        ))}
      </div>

      {/* CTC Impact Projection */}
      {ctc > 0 && (
        <div style={{ border: "1px solid #E2E8F0", borderRadius: 12, overflow: "hidden" }}>
          <div style={{ background: "#0D1B2A", padding: "12px 18px" }}>
            <div style={{ fontFamily: "'Sora',sans-serif", fontWeight: 700, fontSize: 13, color: "#fff" }}>CTC Impact Projection</div>
          </div>
          <div style={{ padding: "16px 18px", display: "grid", gridTemplateColumns: "1fr 1fr 1fr", gap: 12 }}>
            {[
              { l: "Current CTC", v: "₹" + Math.round(ctc).toLocaleString("en-IN") + "/mo", c: "#64748B" },
              { l: hikePct != null ? "Hike Amount (" + hikePct + "%)" : "Hike Amount", v: hikeAmt != null ? "+₹" + Math.round(hikeAmt).toLocaleString("en-IN") + "/mo" : "—", c: "#10B981" },
              { l: "New CTC", v: newCtc != null ? "₹" + Math.round(newCtc).toLocaleString("en-IN") + "/mo" : "—", c: "#E8520A" },
            ].map((item, i) => (
              <div key={i} style={{ background: "#F8FAFC", borderRadius: 9, padding: "12px 14px", textAlign: "center", border: "1px solid #F1F5F9" }}>
                <div style={{ fontSize: 10, color: "#94A3B8", fontWeight: 700, marginBottom: 5, textTransform: "uppercase", letterSpacing: 0.4 }}>{item.l}</div>
                <div style={{ fontFamily: "'JetBrains Mono',monospace", fontWeight: 800, fontSize: 16, color: item.c }}>{item.v}</div>
              </div>
            ))}
          </div>
        </div>
      )}
    </>
  );
}

export default Scoring;